import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams } from 'react-router';
import { 
  ChevronLeft, ChevronRight, ChevronDown, BookOpen, ShieldCheck, 
  Layers, Zap, Info, CheckCircle2, PlayCircle,
  X, HelpCircle, GraduationCap, ArrowRight,
  ClipboardList, AlertTriangle, Timer, Star,
  ShieldAlert, ClipboardCheck, Ban, MessageSquare,
  Hand, Activity, Ruler, Keyboard, ListChecks, Fingerprint, Search, Sparkles, Eye, Image as ImageIcon, Brain, Loader2,
  Trophy, RefreshCw, Award, LayoutDashboard, Printer,
  CheckCircle, Lock, Target, AlertCircle, Maximize2,
  Link as LinkIcon, Volume2, ArrowUp, ArrowDown, RotateCcw, FileText,
  User, HeartPulse, Clock, SlidersHorizontal, Play, Compass, Headphones,
  ExternalLink, Video
} from 'lucide-react';
import { SkillSlide, SkillItemSlide, SkillSlideType, BackendQuestion, ClinicalSimulatorScenario } from '../types';
import { ClinicalRoomStage } from '../components/simulator/ClinicalRoomStage';
import { CLINICAL_ENVIRONMENTS } from '../data/clinicalEnvironments';
import { SkillIconResolver } from '../data/skillsData.js';
import { GamificationService } from '../services/gamificationService';
import { AdUnit } from '../components/AdUnit';
import { AdIntervalBreak } from '../components/AdIntervalBreak';
import { SponsoredService } from '../services/sponsoredService';
import { StorageService } from '../services/storageService';
import { useAuth } from '../components/AuthBridge';
import { ModuleHeader } from '../components/ModuleHeader';
import { AdvanceSuggestionPanel } from '../components/AdvanceSuggestionPanel';
import { useThemeTokens } from '../hooks/useActiveTheme';
import { PageLoader } from '../components/PageLoader';
import { triggerSkillMasteredToast } from '../components/ToastNotification';
import { PrintStudyGuideModal, PrintScope } from '../components/PrintStudyGuideModal';
import { PrintStudyDocument } from '../components/PrintStudyDocument';
import { downloadPDFFromElement } from '../services/pdfService';
import { STUDY_MODULES } from '../data/studyGuideData';
import { getSafeEmbedUrl, getEmbedSandbox, EMBED_ALLOW_POLICY } from '../services/embedService';
import { EmbeddedPhasePlayer } from '../components/EmbeddedPhasePlayer';

// Dynamic category resolver according to skills definition
const getSkillCategory = (skill: SkillSlide) => {
  const name = skill.name.toLowerCase();
  const id = skill.id.toLowerCase();
  
  if (
    name.includes('wash') || name.includes('hygiene') || name.includes('hand') || name.includes('ppe') || name.includes('glove') || name.includes('infection') ||
    id.includes('wash') || id.includes('hygiene') || id.includes('hand') || id.includes('ppe')
  ) {
    return {
      name: 'Safety & Hygiene',
      icon: ShieldCheck,
      color: 'emerald',
      badgeColor: 'bg-emerald-50 border-emerald-100 text-emerald-700 dark:bg-emerald-950/20 dark:border-emerald-900/50 dark:text-emerald-400',
      hoverColor: 'hover:border-emerald-300 dark:hover:border-emerald-700',
      textColor: 'text-emerald-600 dark:text-emerald-400'
    };
  }
  
  if (
    name.includes('pulse') || name.includes('respiration') || name.includes('blood pressure') || name.includes('temp') || name.includes('vital') || name.includes('weight') || name.includes('urine') || name.includes('measurement') || name.includes('measure') || name.includes('output') || name.includes('intake') ||
    id.includes('pulse') || id.includes('respiration') || id.includes('bp') || id.includes('vitals') || id.includes('weight') || id.includes('measure')
  ) {
    return {
      name: 'Vitals & Metrics',
      icon: Activity,
      color: 'teal',
      badgeColor: 'bg-teal-50 border-teal-100 text-teal-700 dark:bg-teal-950/20 dark:border-teal-900/50 dark:text-teal-400',
      hoverColor: 'hover:border-teal-300 dark:hover:border-teal-700',
      textColor: 'text-teal-600 dark:text-teal-400'
    };
  }
  
  if (
    name.includes('ambulat') || name.includes('gait') || name.includes('walk') || name.includes('transfer') || name.includes('wheelchair') || name.includes('range of motion') || name.includes('rom') || name.includes('position') || name.includes('turn') || name.includes('bench') ||
    id.includes('ambulat') || id.includes('gait') || id.includes('transfer') || id.includes('rom') || id.includes('position')
  ) {
    return {
      name: 'Mobility & Transfers',
      icon: Hand,
      color: 'indigo',
      badgeColor: 'bg-indigo-50 border-indigo-100 text-indigo-700 dark:bg-indigo-950/20 dark:border-indigo-900/50 dark:text-indigo-400',
      hoverColor: 'hover:border-indigo-300 dark:hover:border-indigo-700',
      textColor: 'text-indigo-600 dark:text-indigo-400'
    };
  }
  
  if (
    name.includes('bath') || name.includes('mouth') || name.includes('oral') || name.includes('groom') || name.includes('dress') || name.includes('shave') || name.includes('nail') || name.includes('hair') || name.includes('teeth') || name.includes('feed') || name.includes('eat') ||
    id.includes('bath') || id.includes('oral') || id.includes('groom') || id.includes('dress') || id.includes('shave') || id.includes('nail') || id.includes('feed')
  ) {
    return {
      name: 'Personal Care & Grooming',
      icon: BookOpen,
      color: 'amber',
      badgeColor: 'bg-amber-50 border-amber-100 text-amber-700 dark:bg-amber-950/20 dark:border-amber-900/50 dark:text-amber-450',
      hoverColor: 'hover:border-amber-300 dark:hover:border-amber-700',
      textColor: 'text-amber-600 dark:text-amber-450'
    };
  }
  
  if (
    name.includes('bedpan') || name.includes('catheter') || name.includes('perineal') || name.includes('toileting') || name.includes('elimination') || name.includes('peri-care') ||
    id.includes('bedpan') || id.includes('cath') || id.includes('peri') || id.includes('toilet')
  ) {
    return {
      name: 'Toileting & Elimination',
      icon: ClipboardCheck,
      color: 'rose',
      badgeColor: 'bg-rose-50 border-rose-100 text-rose-700 dark:bg-rose-950/20 dark:border-rose-900/50 dark:text-rose-450',
      hoverColor: 'hover:border-rose-300 dark:hover:border-rose-700',
      textColor: 'text-rose-600 dark:text-rose-450'
    };
  }

  return {
    name: 'Supportive Care',
    icon: ListChecks,
    color: 'violet',
    badgeColor: 'bg-violet-50 border-violet-100 text-violet-700 dark:bg-violet-950/20 dark:border-violet-900/50 dark:text-violet-400',
    hoverColor: 'hover:border-violet-300 dark:hover:border-violet-700',
    textColor: 'text-violet-600 dark:text-violet-400'
  };
};

const vendorTips = {
  Credentia: [
    {
      title: "Hand Hygiene Standard",
      text: "Credentia examiners require washing hands with soap and water. Hand sanitizers are only allowed if the instructions say so.",
      critical: true,
      keywords: ["wash", "hygiene", "soap", "water", "handwash"]
    },
    {
      title: "Privacy & Dignity Check",
      text: "Always pull the privacy curtain and knock before entering. This is a critical step to respect the patient.",
      critical: true,
      keywords: ["curtain", "privacy", "drape", "screen", "knock"]
    },
    {
      title: "Wheelchair & Bed Brakes",
      text: "Lock both wheelchair and bed brakes before moving a patient. Leaving brakes unlocked is an automatic fail.",
      critical: true,
      keywords: ["brake", "lock", "wheelchair", "bed"]
    },
    {
      title: "Call Light Placement",
      text: "Make sure to place the call light in the patient's hand before finishing.",
      critical: true,
      keywords: ["call", "light", "signal", "device", "hand"]
    },
    {
      title: "Verbal Signaling",
      text: "Tell the examiner 'I have washed my hands' and 'I am done with the skill' to make sure they write down your score.",
      critical: false,
      keywords: ["verbal", "say", "report", "state", "complete", "mouth"]
    }
  ],
  Prometric: [
    {
      title: "20-Second Friction Scrub",
      text: "Scrub your hands with soap for at least 20 seconds. Examiners will time this.",
      critical: true,
      keywords: ["seconds", "wash", "soap", "friction", "20", "twenty", "handwash"]
    },
    {
      title: "Indirect Care Skills",
      text: "Prometric grades you on safety, patient rights, communication, and infection control, alongside the physical steps.",
      critical: true,
      keywords: ["indirect", "right", "communicate", "explain", "consent", "safety"]
    },
    {
      title: "Clean Supplies Barrier",
      text: "Place a clean paper towel on the table before putting down your supplies.",
      critical: true,
      keywords: ["barrier", "towel", "table", "surface", "paper"]
    },
    {
      title: "Glove Removal Sequence",
      text: "Take off gloves by pinching the palm of one glove, peeling it off, then sliding a finger under the remaining cuff.",
      critical: true,
      keywords: ["glove", "remove", "pinch", "cuff", "dispose", "trash"]
    },
    {
      title: "Explain the Procedure",
      text: "Face the patient and explain what you are going to do before you touch them.",
      critical: false,
      keywords: ["explain", "procedure", "introduce", "resident", "client"]
    }
  ],
  Headmaster: [
    {
      title: "Mechanical Height Safety",
      text: "Adjust the bed to a safe height while working. You must lower it and raise side rails before leaving.",
      critical: true,
      keywords: ["bed", "height", "lower", "lowest", "raise", "rail", "working"]
    },
    {
      title: "Clinical Measurement Bounds",
      text: "Measurements must be accurate. Your reading must match the examiner's closely (pulse +/- 2, breathing +/- 2, urine +/- 25ml).",
      critical: true,
      keywords: ["measur", "record", "tolerance", "value", "vitals", "pulse", "respiration", "output"]
    },
    {
      title: "Check Resident Alignment",
      text: "Make sure the patient is comfortable and sitting or lying straight when you finish.",
      critical: true,
      keywords: ["align", "comfort", "posture", "support", "pillow", "position"]
    },
    {
      title: "Avoid Table Contamination",
      text: "Keep dirty towels and linen away from clean areas and your uniform.",
      critical: true,
      keywords: ["linen", "dirty", "soiled", "washcloth", "away", "scrubs", "body"]
    },
    {
      title: "Verbal Correction",
      text: "If you miss a step, you can tell the examiner out loud to correct it before saying you are finished.",
      critical: false,
      keywords: ["correct", "miss", "verbal", "recall", "examiner", "completed"]
    }
  ]
};

export interface ClinicalPracticeStep {
  id: string;
  text: string;
  originalIndex: number;
  title: string;
  phase: 'Preparation' | 'Core Procedure' | 'Safety & Exit';
  isCritical: boolean;
  criticalPoints?: string;
}

export const extractCuratedSteps = (skill: SkillSlide): ClinicalPracticeStep[] => {
  if (!skill || !skill.slides) return [];

  const rawList: { text: string; slideTitle: string; criticalPoints?: string; slideType: string }[] = [];

  const criticalItemsFromSlide: string[] = [];
  skill.slides.forEach(slide => {
    if (slide.type === 'criticals' && slide.data?.items) {
      slide.data.items.forEach((ci: string) => criticalItemsFromSlide.push(ci.toLowerCase()));
    }
  });

  skill.slides.forEach((slide) => {
    if (slide.type === 'procedure' && slide.data?.steps) {
      slide.data.steps.forEach((stepText: string) => {
        if (stepText && stepText.trim().length > 0) {
          rawList.push({
            text: stepText.trim(),
            slideTitle: slide.title || '',
            criticalPoints: slide.data.criticalPoints,
            slideType: slide.type
          });
        }
      });
    }
  });

  if (rawList.length === 0) return [];

  const totalRaw = rawList.length;

  // Track occurrences to detect & disambiguate repeated steps
  const textCountMap = new Map<string, number>();
  rawList.forEach((item) => {
    const norm = item.text.toLowerCase().replace(/[^a-z0-9]/g, ' ').replace(/\s+/g, ' ').trim();
    textCountMap.set(norm, (textCountMap.get(norm) || 0) + 1);
  });

  const occurrenceTracker = new Map<string, number>();
  const curated: ClinicalPracticeStep[] = [];
  const seenExactTextInPhase = new Set<string>();

  rawList.forEach((item, idx) => {
    const rawLower = item.text.toLowerCase();
    const titleLower = item.slideTitle.toLowerCase();
    const norm = rawLower.replace(/[^a-z0-9]/g, ' ').replace(/\s+/g, ' ').trim();

    // 1. Determine Phase
    let phase: 'Preparation' | 'Core Procedure' | 'Safety & Exit' = 'Core Procedure';
    const isEarly = idx < Math.max(2, Math.floor(totalRaw * 0.35));
    const isLate = idx >= Math.max(totalRaw - 3, Math.floor(totalRaw * 0.70));

    const prepKeywords = ['knock', 'introduce', 'identity', 'identif', 'privacy', 'curtain', 'supplies', 'gather', 'wash hand', 'hygiene', 'glove', 'barrier', 'explain', 'water temp'];
    const exitKeywords = ['call light', 'signal', 'bed to lowest', 'lowest position', 'brakes locked', 'lock bed', 'wash hand', 'hygiene', 'clean up', 'dispose', 'record', 'report', 'chart', 'document'];

    if (titleLower.includes('prep') || titleLower.includes('phase 1') || titleLower.includes('beginning') || (isEarly && prepKeywords.some(k => rawLower.includes(k)))) {
      phase = 'Preparation';
    } else if (titleLower.includes('completion') || titleLower.includes('final') || titleLower.includes('ending') || titleLower.includes('exit') || titleLower.includes('reporting') || (isLate && exitKeywords.some(k => rawLower.includes(k)))) {
      phase = 'Safety & Exit';
    } else {
      phase = 'Core Procedure';
    }

    // Deduplicate identical steps within the exact same phase
    const phaseKey = `${phase}:${norm}`;
    if (seenExactTextInPhase.has(phaseKey)) {
      return; // Skip duplicate identical step in the same phase
    }
    seenExactTextInPhase.add(phaseKey);

    // 2. Disambiguate repeated steps across different phases (e.g., Hand Hygiene)
    let displayText = item.text;
    const count = textCountMap.get(norm) || 1;
    if (count > 1) {
      const occIndex = (occurrenceTracker.get(norm) || 0) + 1;
      occurrenceTracker.set(norm, occIndex);

      if (rawLower.includes('wash') || rawLower.includes('hygiene') || rawLower.includes('hand')) {
        if (phase === 'Preparation') {
          displayText = `[Initial Safety] Perform hand hygiene before resident contact`;
        } else if (phase === 'Safety & Exit') {
          displayText = `[Exit Protocol] Perform terminal hand hygiene after care & cleanup`;
        } else {
          displayText = `[Procedure Check] Perform hand hygiene & glove change`;
        }
      } else if (rawLower.includes('privacy')) {
        displayText = phase === 'Preparation' 
          ? `[Initial Safety] Provide resident privacy (pull curtain / close door)`
          : `[Exit Protocol] Open privacy curtain if requested by resident`;
      } else if (rawLower.includes('call light') || rawLower.includes('signal')) {
        displayText = `[Exit Protocol] Place call light within resident's direct, reachable grasp`;
      } else {
        displayText = `[${phase}] ${item.text}`;
      }
    }

    // 3. Detect Critical Safety Step
    const criticalKeywords = [
      '20 second', 'friction', 'lower than elbow', 'barrier towel', 'faucet with',
      'lock brake', 'brakes locked', 'non-skid', 'gait belt', 'thumb side', 'two finger',
      'three finger', 'full 60 second', 'not use thumb', 'do not use thumb', 'front to back',
      'water temp', 'lowest position', 'call light in reach', 'curtain'
    ];

    const isExplicitlyCritical = !!item.criticalPoints;
    const matchesCriticalSlide = criticalItemsFromSlide.some(ci => norm.includes(ci) || ci.includes(norm));
    const matchesCriticalKeyword = criticalKeywords.some(ck => rawLower.includes(ck));

    const isCritical = isExplicitlyCritical || matchesCriticalSlide || matchesCriticalKeyword;

    let criticalPoints = item.criticalPoints;
    if (isCritical && !criticalPoints) {
      if (rawLower.includes('lock')) criticalPoints = 'Mandatory Safety Rule: Brakes must be firmly locked prior to any resident movement.';
      else if (rawLower.includes('20 second')) criticalPoints = 'Infection Control Rule: Must lather vigorously with friction for at least 20 seconds.';
      else if (rawLower.includes('barrier')) criticalPoints = 'Contamination Control: Must utilize a dry clean barrier towel to operate water faucet.';
      else if (rawLower.includes('non-skid')) criticalPoints = 'Fall Prevention Standard: Resident must wear non-skid footwear before standing.';
      else if (rawLower.includes('call light')) criticalPoints = 'Resident Safety Standard: Call light must be placed within easy direct reach.';
      else if (rawLower.includes('60 second') || rawLower.includes('thumb')) criticalPoints = 'Clinical Accuracy: Count pulse for a full 60 seconds without using thumb.';
      else criticalPoints = 'Critical Board Milestone: Mandatory compliance checkpoint.';
    }

    curated.push({
      id: `${skill.id}-step-${curated.length}`,
      text: displayText,
      originalIndex: curated.length,
      title: item.slideTitle || phase,
      phase,
      isCritical,
      criticalPoints
    });
  });

  return curated;
};

export const getSimulatedPatientInfo = (skill: SkillSlide, variation: string) => {
  const nameLower = (skill?.name || '').toLowerCase();
  let residentName = "Mrs. Eleanor Vance";
  let roomNumber = "Room 104-B";
  let condition = "Post-Stroke Recovery • Standby Assist";
  let precaution = "Standard Fall Precautions";

  if (nameLower.includes('wash') || nameLower.includes('hygiene') || nameLower.includes('ppe')) {
    residentName = "Clinical Hand Hygiene Station";
    roomNumber = "Prep Sink #2";
    condition = "Standard Barrier & Infection Control Protocol";
    precaution = "Clean Technique Mandatory";
  } else if (nameLower.includes('pulse') || nameLower.includes('respiration') || nameLower.includes('vital') || nameLower.includes('blood pressure')) {
    residentName = "Mr. Robert Chen";
    roomNumber = "Room 208-A";
    condition = "Baseline Vitals Monitoring • Cardiac Floor";
    precaution = "Full 60-Second Count Protocol";
  } else if (nameLower.includes('ambulat') || nameLower.includes('gait') || nameLower.includes('transfer') || nameLower.includes('wheelchair')) {
    residentName = "Mrs. Dorothy Campbell";
    roomNumber = "Room 312";
    condition = "High Fall Risk • Left-Sided Hemiparesis";
    precaution = "Gait Belt Mandatory • Non-Skid Footwear";
  } else if (nameLower.includes('bath') || nameLower.includes('perineal') || nameLower.includes('bedpan') || nameLower.includes('catheter')) {
    residentName = "Mr. George Harrison";
    roomNumber = "Room 115-B";
    condition = "Total Bed Rest • Sensitive Skin Protocol";
    precaution = "Strict Privacy Required • Water Temp Check";
  }

  if (variation === 'unresponsive') {
    condition = "Comatose / Unresponsive • Vocalize All Actions Loudly";
  } else if (variation === 'combative') {
    condition = "Agitated / Restless • Maintain Calm Voice & Gentle Pace";
  } else if (variation === 'left_weakness') {
    condition = "Severe Left-Sided Weakness • Dress/Undress Weak Side Protocol";
  }

  return { residentName, roomNumber, condition, precaution };
};

export const SkillsLab: React.FC = () => {
  const { certId } = useParams<{ certId: string }>(); 
  const { activeCert, user } = useAuth();
  const [config, setConfig] = useState(SponsoredService.getConfig());
  const tokens = useThemeTokens();

  useEffect(() => {
    const handleUpdate = () => {
      setConfig(SponsoredService.getConfig());
    };
    window.addEventListener('sponsored-config-updated', handleUpdate);
    return () => window.removeEventListener('sponsored-config-updated', handleUpdate);
  }, []);
  const [skills, setSkills] = useState<SkillSlide[]>([]);
  const [selectedSkill, setSelectedSkill] = useState<SkillSlide | null>(null);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [completedSkills, setCompletedSkills] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategoryTab, setActiveCategoryTab] = useState<string>('all');
  const [isLoading, setIsLoading] = useState(true);
  const [isFetchingFullSkill, setIsFetchingFullSkill] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [expandedImage, setExpandedImage] = useState<string | null>(null);
  const [showAdBreak, setShowAdBreak] = useState(false);
  const [lastAdIndex, setLastAdIndex] = useState(-1);
  const [isSpeaking, setIsSpeaking] = useState(false);

  // Print Modal State
  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
  const [printScope, setPrintScope] = useState<PrintScope>('skills_lab');
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  const handleConfirmPrint = (scope: PrintScope) => {
    setPrintScope(scope);
    setIsPrintModalOpen(false);
    setTimeout(() => {
      window.print();
      if (window.trackEvent) window.trackEvent('skills_lab_printed', 'skills', scope);
    }, 150);
  };

  const handleConfirmDownloadPdf = async (scope: PrintScope) => {
    setPrintScope(scope);
    setIsGeneratingPdf(true);
    setTimeout(async () => {
      try {
        const fileName = selectedSkill && scope === 'current'
          ? `CNA_Skill_Procedure_${selectedSkill.name.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`
          : scope === 'study_guide'
          ? 'CNA_Study_Guide.pdf'
          : 'CNA_Clinical_Skills_Manual.pdf';

        await downloadPDFFromElement('print-study-document', { fileName });
        if (window.trackEvent) window.trackEvent('skills_lab_pdf_downloaded', 'skills', scope);
      } catch (err) {
        console.error('Failed to generate skills lab PDF:', err);
      } finally {
        setIsGeneratingPdf(false);
        setIsPrintModalOpen(false);
      }
    }, 120);
  };

  // Quiz State
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizScore, setQuizScore] = useState(0);
  const [quizIndex, setQuizIndex] = useState(0);
  const [isQuizCompleted, setIsQuizCompleted] = useState(false);
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [isAnswered, setIsAnswered] = useState(false);

  // Clinical Exam Practice Mode State
  const [practiceMode, setPracticeMode] = useState(false);
  const [practiceSelectedSteps, setPracticeSelectedSteps] = useState<ClinicalPracticeStep[]>([]);
  const [practiceAvailableSteps, setPracticeAvailableSteps] = useState<ClinicalPracticeStep[]>([]);
  const [practiceResult, setPracticeResult] = useState<{
    score: number;
    passed: boolean;
    missedCritical: boolean;
    criticalPointText?: string;
    analysis: { text: string; originalIndex: number; status: 'correct' | 'wrong' | 'unplaced' }[];
    feedback: string[];
  } | null>(null);
  const [isEvaluatingPractice, setIsEvaluatingPractice] = useState(false);
  const [practiceVendor, setPracticeVendor] = useState<'Credentia' | 'Prometric' | 'Headmaster'>('Credentia');
  const [showQuickTips, setShowQuickTips] = useState(true);
  const [practiceDemoMode, setPracticeDemoMode] = useState(false);

  // New Test Mode & View Progressive Disclosure States
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  const [showSafetyRulesBanner, setShowSafetyRulesBanner] = useState(false);
  const [poolSortOrder, setPoolSortOrder] = useState<'shuffled' | 'alphabetical' | 'original'>('shuffled');
  const [immediateFailMode, setImmediateFailMode] = useState(false);
  const [practiceTimerSeconds, setPracticeTimerSeconds] = useState(300);
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [scenarioVariation, setScenarioVariation] = useState<'standard' | 'unresponsive' | 'combative' | 'left_weakness'>('standard');
  const [isListeningVoice, setIsListeningVoice] = useState(false);
  const [voiceFeedbackMsg, setVoiceFeedbackMsg] = useState("");
  const recognitionRef = useRef<any>(null);

  // Modern Study & Test Mode state enhancements
  const [studyViewMode, setStudyViewMode] = useState<'slides' | 'rubric'>('slides');
  const [practiceStepFilter, setPracticeStepFilter] = useState<'all' | 'Preparation' | 'Core Procedure' | 'Safety & Exit'>('all');
  const [practiceStepSearch, setPracticeStepSearch] = useState('');
  const [speechSpeed, setSpeechSpeed] = useState<number>(1.0);
  const [practiceExamStyle, setPracticeExamStyle] = useState<'guided' | 'exam'>('guided');

  // Clinical Simulator Engine State
  const [simulatorMode, setSimulatorMode] = useState(false);
  const [clinicalScenarios, setClinicalScenarios] = useState<ClinicalSimulatorScenario[]>([]);
  const [activeClinicalScenario, setActiveClinicalScenario] = useState<ClinicalSimulatorScenario | null>(null);
  const [autoStartTourOnLaunch, setAutoStartTourOnLaunch] = useState(false);
  const [showSkillsLabTourPreview, setShowSkillsLabTourPreview] = useState(false);
  const skillsLabTourPreviewRef = useRef<HTMLDivElement>(null);

  // Close SkillsLab tour preview on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (skillsLabTourPreviewRef.current && !skillsLabTourPreviewRef.current.contains(e.target as Node)) {
        setShowSkillsLabTourPreview(false);
      }
    };
    if (showSkillsLabTourPreview) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSkillsLabTourPreview]);

  // Stop all audio/speech when component unmounts or browser tab is hidden
  useEffect(() => {
    const handleVis = () => {
      if (document.hidden) {
        if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
          window.speechSynthesis.cancel();
        }
        setIsSpeaking(false);
      }
    };
    document.addEventListener('visibilitychange', handleVis);
    window.addEventListener('pagehide', handleVis);
    return () => {
      document.removeEventListener('visibilitychange', handleVis);
      window.removeEventListener('pagehide', handleVis);
      if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  // When selected skill changes or is cleared, stop any running speech/tour
  useEffect(() => {
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }
    setIsSpeaking(false);
    setAutoStartTourOnLaunch(false);
  }, [selectedSkill?.id]);

  // When exiting simulator mode, ensure tour flag is reset and speech is stopped
  useEffect(() => {
    if (!simulatorMode) {
      if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
        window.speechSynthesis.cancel();
      }
      setIsSpeaking(false);
      setAutoStartTourOnLaunch(false);
    }
  }, [simulatorMode]);

  const touchStart = useRef<{ x: number, y: number } | null>(null);
  const certPrefix = certId || 'cna';

  // Feature Flag: Check if Clinical Simulator is active for platform and cert
  const isClinicalSimulatorActive = 
    config?.isClinicalSimulatorEnabled !== false &&
    (config?.clinicalSimulatorEnabledCerts || ['cna', 'phlebotomy', 'medical_assistant', 'pharmacy_tech', 'pct']).includes(certPrefix);

  useEffect(() => {
    const loadSummary = async () => {
        setIsLoading(true);
        try {
            const [data, scenarios] = await Promise.all([
              StorageService.getSkillsSummary(certPrefix),
              StorageService.getClinicalSimulators(certPrefix).catch(() => [])
            ]);
            setSkills(data || []);
            setClinicalScenarios(scenarios || []);
        } catch (err) {
            console.error("Failed to load skill summaries:", err);
        } finally {
            setIsLoading(false);
        }
    };
    loadSummary();
    const saved = localStorage.getItem(`${certPrefix}_completed_skills`);
    if (saved) setCompletedSkills(JSON.parse(saved));
    else setCompletedSkills([]);
  }, [certPrefix]);

  // Reactively synchronize clinical scenarios and skills when attached, detached, or updated in Studio
  useEffect(() => {
    const handleSimulatorsUpdate = async () => {
      try {
        const scenarios = await StorageService.getClinicalSimulators(certPrefix);
        setClinicalScenarios(scenarios || []);
      } catch (err) {}
    };
    const handleSkillsUpdate = async (e: any) => {
      const incoming = e.detail?.skill;
      if (incoming && incoming.id) {
        setSkills(prev => prev.map(s => s.id === incoming.id ? { ...s, ...incoming } : s));
        setSelectedSkill(prev => (prev && prev.id === incoming.id ? { ...prev, ...incoming } : prev));
      } else {
        try {
          const freshSkills = await StorageService.getSkillsSummary(certPrefix);
          setSkills(freshSkills || []);
        } catch (err) {}
      }
    };
    window.addEventListener('clinical-simulators-updated', handleSimulatorsUpdate);
    window.addEventListener('skills-updated', handleSkillsUpdate);
    return () => {
      window.removeEventListener('clinical-simulators-updated', handleSimulatorsUpdate);
      window.removeEventListener('skills-updated', handleSkillsUpdate);
    };
  }, [certPrefix]);

  useEffect(() => {
    if (selectedSkill) {
      window.dispatchEvent(
        new CustomEvent('update-breadcrumbs-nesting', {
          detail: {
            currentPageKey: 'skills',
            level2Label: selectedSkill.name,
            onBackAction: 'skills-back-action',
          },
        })
      );
    } else {
      window.dispatchEvent(
        new CustomEvent('update-breadcrumbs-nesting', {
          detail: null,
        })
      );
    }
  }, [selectedSkill]);

  useEffect(() => {
    const handleBack = () => {
      handleClose();
    };
    window.addEventListener('skills-back-action', handleBack);
    return () => {
      window.removeEventListener('skills-back-action', handleBack);
    };
  }, [selectedSkill]);

  // Check whether a skill has an explicitly authored simulation scenario attached
  const getScenarioForSkill = useCallback((skillToCheck?: SkillSlide | any): ClinicalSimulatorScenario | undefined => {
    if (!skillToCheck || !skillToCheck.id) return undefined;
    const targetId = (skillToCheck.id || '').toLowerCase().trim();

    // STRICT GUARD: Must explicitly have hasSimulator === true and not be disabled
    if (skillToCheck.hasSimulator !== true || skillToCheck.isSimulatorEnabled === false) {
      return undefined;
    }

    const isAdmin = user?.role === 'admin';

    // 1. Direct explicit link via skill.simulatorScenarioId pointer (HIGHEST PRIORITY)
    if (skillToCheck.simulatorScenarioId && typeof skillToCheck.simulatorScenarioId === 'string' && skillToCheck.simulatorScenarioId.trim() !== '') {
      const directMatch = clinicalScenarios.find(s => 
        s && s.id === skillToCheck.simulatorScenarioId && 
        s.status !== 'archived' && 
        (isAdmin || s.status === 'published')
      );
      if (directMatch) return directMatch;
    }

    // 2. Reverse link: Check if an active published scenario explicitly designates this skillId
    return clinicalScenarios.find(s => {
      if (!s || !s.id) return false;
      if (s.status === 'archived') return false;

      // In student / user mode (non-admin), draft scenarios are hidden
      if (!isAdmin && s.status !== 'published') return false;

      const scSkillId = (s.skillId || '').toLowerCase().trim();
      if (scSkillId && scSkillId !== '' && scSkillId !== 'none' && scSkillId !== 'null' && scSkillId !== 'unassigned') {
        if (scSkillId === targetId) return true;
        // Normalize certification prefixes if present (e.g. cna-skill-xxx vs xxx)
        const cleanScSkillId = scSkillId.replace(/^[a-z_]+-skill-/, '');
        const cleanTargetId = targetId.replace(/^[a-z_]+-skill-/, '');
        if (cleanScSkillId && cleanScSkillId === cleanTargetId) return true;
      }

      return false;
    });
  }, [clinicalScenarios, user?.role]);

  const hasScenarioForSkill = useCallback((skillToCheck?: SkillSlide | any): boolean => {
    return Boolean(getScenarioForSkill(skillToCheck));
  }, [getScenarioForSkill]);

  const initiateSimulatorMode = useCallback((targetSkill?: SkillSlide | any, startTour: boolean = false) => {
    const skillToUse = (targetSkill && typeof targetSkill === 'object' && Array.isArray(targetSkill.slides))
      ? targetSkill
      : selectedSkill;
    if (!skillToUse) return;

    // Locate matching clinical simulator scenario that was created and attached to this skill
    const match = getScenarioForSkill(skillToUse);

    if (match) {
      setActiveClinicalScenario(match);
      setSimulatorMode(true);
      setPracticeMode(false);
      setAutoStartTourOnLaunch(startTour);
    } else {
      console.warn("No clinical simulation scenario created or attached for skill:", skillToUse.name);
      setSimulatorMode(false);
      setAutoStartTourOnLaunch(false);
    }
  }, [selectedSkill, getScenarioForSkill]);

  const activeScenario = useMemo(() => {
    if (!selectedSkill) return null;
    return getScenarioForSkill(selectedSkill);
  }, [selectedSkill, getScenarioForSkill]);

  const scenarioPreviewImage = useMemo(() => {
    if (activeScenario?.imageUrl) return activeScenario.imageUrl;
    if (activeScenario?.photosphereUrl) return activeScenario.photosphereUrl;
    if (activeScenario?.environment && CLINICAL_ENVIRONMENTS[activeScenario.environment]?.imageUrl) {
      return CLINICAL_ENVIRONMENTS[activeScenario.environment].imageUrl;
    }
    return '/images/environments/bedside_room.jpg';
  }, [activeScenario]);

  const scenarioStationCount = useMemo(() => {
    if (!activeScenario?.hotspots) return 0;
    return activeScenario.hotspots.length;
  }, [activeScenario]);

  const handleOpenSkill = async (summary: SkillSlide, targetMode: 'study' | 'test' | 'simulator' = 'study') => {
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }
    setIsSpeaking(false);
    setAutoStartTourOnLaunch(false);
    setIsFetchingFullSkill(true);
    try {
        const fullSkill = await StorageService.getSkillById(certPrefix, summary.id);
        setSelectedSkill(fullSkill);
        setCurrentSlideIndex(0);
        setLastAdIndex(-1);
        setShowAdBreak(false);
        setShowQuiz(false);
        setIsQuizCompleted(false);
        setQuizScore(0);
        setQuizIndex(0);
        setShowHint(false);
        
        // Reset Clinical Practice & Simulator states
        setPracticeMode(targetMode === 'test');
        setSimulatorMode(targetMode === 'simulator');
        setPracticeSelectedSteps([]);
        setPracticeAvailableSteps([]);
        setPracticeResult(null);
        setIsEvaluatingPractice(false);

        if (targetMode === 'test') {
          const curated = extractCuratedSteps(fullSkill);
          const shuffled = [...curated].sort(() => Math.random() - 0.5);
          setPracticeAvailableSteps(shuffled);
          setPracticeTimerSeconds(300);
          setIsTimerRunning(true);
          setPracticeStepFilter('all');
          setPracticeStepSearch('');
        } else if (targetMode === 'simulator') {
          initiateSimulatorMode(fullSkill);
        }

        window.scrollTo({ top: 0, behavior: 'smooth' });

        if (window.trackEvent) window.trackEvent('skill_procedure_opened', 'skill_lab', fullSkill.name);
    } catch (err) {
        console.error("Failed to hydrate skill details:", err);
    } finally {
        setIsFetchingFullSkill(false);
    }
  };

  const handleClose = () => {
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }
    setIsSpeaking(false);
    setAutoStartTourOnLaunch(false);
    setSelectedSkill(null);
    setShowQuiz(false);
    setShowAdBreak(false);
    setPracticeMode(false);
    setSimulatorMode(false);
    setActiveClinicalScenario(null);
    setPracticeSelectedSteps([]);
    setPracticeAvailableSteps([]);
    setPracticeResult(null);
    setIsEvaluatingPractice(false);
    setIsTimerRunning(false);
    setIsListeningVoice(false);
  };

  const getCorrectStepsList = useCallback((): ClinicalPracticeStep[] => {
    if (!selectedSkill) return [];
    return extractCuratedSteps(selectedSkill);
  }, [selectedSkill]);

  const initiatePracticeMode = useCallback((targetSkill?: SkillSlide | any) => {
    // Ensure targetSkill is a genuine SkillSlide and not an event object or synthetic event
    const skillToUse = (targetSkill && typeof targetSkill === 'object' && Array.isArray(targetSkill.slides))
      ? targetSkill
      : selectedSkill;
    if (!skillToUse) return;
    
    const curatedList = extractCuratedSteps(skillToUse);

    // Shuffle stepsList using Fisher-Yates
    const shuffled = [...curatedList].sort(() => Math.random() - 0.5);
    
    setPracticeAvailableSteps(shuffled);
    setPracticeSelectedSteps([]);
    setPracticeResult(null);
    setIsEvaluatingPractice(false);
    setPracticeMode(true);
    setPracticeStepFilter('all');
    setPracticeStepSearch('');
    
    // Reset Enhancements
    setPoolSortOrder('shuffled');
    setPracticeTimerSeconds(300); // 5 minutes
    setIsTimerRunning(true);
    setVoiceFeedbackMsg("");
    setIsListeningVoice(false);
    
    // Auto design target vendor based on user state standard if possible, else standard
    if (user?.state === 'FL' || user?.state === 'TX' || user?.state === 'NC') {
      setPracticeVendor('Credentia');
    } else if (user?.state === 'NY' || user?.state === 'CA' || user?.state === 'IL') {
      setPracticeVendor('Prometric');
    } else {
      setPracticeVendor('Headmaster');
    }
    
    if (window.trackEvent) window.trackEvent('skill_practice_mode_initiated', 'skill_lab', skillToUse.name);
  }, [selectedSkill, user?.state]);

  // Self-healing safeguard: If test mode is active but step pools are uninitialized, populate them
  useEffect(() => {
    if (practiceMode && selectedSkill && practiceAvailableSteps.length === 0 && practiceSelectedSteps.length === 0 && !practiceResult) {
      const curated = extractCuratedSteps(selectedSkill);
      if (curated.length > 0) {
        setPracticeAvailableSteps([...curated].sort(() => Math.random() - 0.5));
      }
    }
  }, [practiceMode, selectedSkill, practiceAvailableSteps.length, practiceSelectedSteps.length, practiceResult]);

  const retryMisplacedSteps = () => {
    if (!practiceResult) return;
    const keptSteps: ClinicalPracticeStep[] = [];
    const unplacedOrWrongSteps: ClinicalPracticeStep[] = [];

    practiceSelectedSteps.forEach((step, idx) => {
      if (step.originalIndex === idx) {
        keptSteps.push(step);
      } else {
        unplacedOrWrongSteps.push(step);
      }
    });

    const allAvailable = [...practiceAvailableSteps, ...unplacedOrWrongSteps];
    const uniquePool = Array.from(new Map(allAvailable.map(s => [s.id, s])).values());

    setPracticeSelectedSteps(keptSteps);
    setPracticeAvailableSteps(uniquePool.sort(() => Math.random() - 0.5));
    setPracticeResult(null);
    setPracticeTimerSeconds(300);
    setIsTimerRunning(true);
  };

  const addStepToSelection = (step: any) => {
    setPracticeAvailableSteps(prev => prev.filter(s => s.id !== step.id));
    const newSelected = [...practiceSelectedSteps, step];
    setPracticeSelectedSteps(newSelected);
    setPracticeResult(null);

    // Immediate Fail Simulation logic
    if (immediateFailMode && !!step.criticalPoints) {
      const currentIdx = newSelected.length - 1;
      if (step.originalIndex !== currentIdx) {
        setIsTimerRunning(false);
        const correctStepsList = getCorrectStepsList();
        const stepAnalysis = correctStepsList.map((correctStep) => {
          const placedIdx = newSelected.findIndex(s => s.originalIndex === correctStep.originalIndex);
          let status: 'correct' | 'wrong' | 'unplaced' = 'unplaced';
          if (placedIdx !== -1) {
            status = placedIdx === correctStep.originalIndex ? 'correct' : 'wrong';
          }
          return {
            text: correctStep.text,
            originalIndex: correctStep.originalIndex,
            status
          };
        });

        setPracticeResult({
          score: Math.round((stepAnalysis.filter(s => s.status === 'correct').length / correctStepsList.length) * 100),
          passed: false,
          missedCritical: true,
          criticalPointText: step.criticalPoints || "Critical step performed out of order or omitted.",
          analysis: stepAnalysis,
          feedback: [
            `🚨 IMMEDIATE EXAM ROOM DISQUALIFICATION (${practiceVendor} standard):`,
            `Critical procedural infraction! You placed the critical safety step: "${step.text}" out of sequence (placed as Step #${currentIdx + 1} but should have been Step #${step.originalIndex + 1}).`,
            `Safety Rule Details: "${step.criticalPoints}"`,
            `Status: Attempt Terminated. You must restart the exercise to satisfy compliance standards.`
          ]
        });
      }
    }
  };

  const removeStepFromSelection = (step: any) => {
    setPracticeSelectedSteps(prev => prev.filter(s => s.id !== step.id));
    setPracticeAvailableSteps(prev => [...prev, step]);
    setPracticeResult(null);
  };

  const moveStepUp = (idx: number) => {
    if (idx === 0) return;
    setPracticeSelectedSteps(prev => {
      const copy = [...prev];
      const temp = copy[idx - 1];
      copy[idx - 1] = copy[idx];
      copy[idx] = temp;
      return copy;
    });
    setPracticeResult(null);
  };

  const moveStepDown = (idx: number) => {
    setPracticeSelectedSteps(prev => {
      if (idx === prev.length - 1) return prev;
      const copy = [...prev];
      const temp = copy[idx + 1];
      copy[idx + 1] = copy[idx];
      copy[idx] = temp;
      return copy;
    });
    setPracticeResult(null);
  };

  const evaluatePracticeOrder = useCallback(() => {
    if (!selectedSkill) return;
    setIsEvaluatingPractice(true);
    setIsTimerRunning(false); // Stop the countdown timer during evaluation
    
    // Create actual correct steps list to examine missed steps
    const correctStepsList = getCorrectStepsList();
    const totalSteps = correctStepsList.length;
    if (totalSteps === 0) {
      setIsEvaluatingPractice(false);
      return;
    }

    setTimeout(() => {
      let correctMatches = 0;
      let hasMissedCritical = false;
      let criticalPointText = "";
      const feedbackLines: string[] = [];

      const stepAnalysis = correctStepsList.map((correctStep) => {
        // Find if this step is placed in the selected list
        const placedIdx = practiceSelectedSteps.findIndex(s => s.originalIndex === correctStep.originalIndex);
        
        let status: 'correct' | 'wrong' | 'unplaced' = 'unplaced';
        if (placedIdx !== -1) {
          if (placedIdx === correctStep.originalIndex) {
            status = 'correct';
            correctMatches++;
          } else {
            status = 'wrong';
            feedbackLines.push(`Step "${correctStep.text.slice(0, 50)}..." was placed as Step #${placedIdx + 1}, but should be Step #${correctStep.originalIndex + 1}.`);
          }
        } else {
          feedbackLines.push(`Critical action step was neglected: "${correctStep.text}"`);
        }

        // Check if it's a critical step and is misplaced or unplaced
        if (correctStep.criticalPoints && status !== 'correct') {
          hasMissedCritical = true;
          criticalPointText = correctStep.criticalPoints;
        }

        return {
          text: correctStep.text,
          originalIndex: correctStep.originalIndex,
          status
        };
      });

      const finalScore = Math.round((correctMatches / totalSteps) * 100);
      const isPassed = finalScore >= 80 && !hasMissedCritical;

      // Add general evaluation feedback based on vendor standard
      if (practiceVendor === 'Credentia') {
        feedbackLines.unshift(`Credentia Exam Room Evaluator: High integrity patient safety protocols. ${isPassed ? 'Safety standards approved.' : 'Credentialing examiners require rigorous equipment lockdown and privacy drape placement before beginning.'}`);
      } else if (practiceVendor === 'Prometric') {
        feedbackLines.unshift(`Prometric Exam Room Evaluator: Mandatory clinical checkpoint flow. ${isPassed ? 'Clinical competency validated.' : 'Prometric testers require direct compliance checks, proper hand rub timing, and strict order tracking.'}`);
      } else {
        feedbackLines.unshift(`Headmaster Exam Room Evaluator: Rigorous logical step sequencing. ${isPassed ? 'Clinical skills guidelines satisfied.' : 'Headmaster board grading scales penalize missed physical boundaries or documentation steps.'}`);
      }

      // Add scenario-specific feedback
      if (scenarioVariation === 'unresponsive') {
        feedbackLines.push("🔊 Comatose Resident Scenario Compliance: Ensured continuous vocalization routines and clear, loud step declaration before touch.");
      } else if (scenarioVariation === 'combative') {
        feedbackLines.push("🧘 Restless Resident Scenario Compliance: Verified slow execution interval, reassurance dialog checks, and lowered vocal volume.");
      } else if (scenarioVariation === 'left_weakness') {
        feedbackLines.push("👉 Left Hemiplegia Scenario Compliance: Dressings must be routed starting on the weak left limb. Verification passed.");
      } else {
        feedbackLines.push("👤 Standard Scenario Compliance: Standardized adult clinical sequence validated.");
      }

      // If they passed, stop the timer permanently
      setIsTimerRunning(false);

      setPracticeResult({
        score: finalScore,
         passed: isPassed,
        missedCritical: hasMissedCritical,
        criticalPointText,
        analysis: stepAnalysis,
        feedback: feedbackLines
      });

      setIsEvaluatingPractice(false);
      
      if (isPassed) {
        GamificationService.addPoints(75);
      }
    }, 1500);
  }, [selectedSkill, practiceSelectedSteps, practiceVendor, scenarioVariation, getCorrectStepsList]);

  // Countdown clock tick handler
  useEffect(() => {
    let interval: any = null;
    if (isTimerRunning && practiceTimerSeconds > 0) {
      interval = setInterval(() => {
        setPracticeTimerSeconds((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            setIsTimerRunning(false);
            // Trigger automatic grading when time runs out!
            evaluatePracticeOrder();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isTimerRunning, practiceTimerSeconds, evaluatePracticeOrder]);

  // Voice Sequencing Practice Speech handler
  const startVoiceListening = () => {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setVoiceFeedbackMsg("❌ Web Speech API is not supported in this browser. Please use Google Chrome or Apple Safari.");
      return;
    }

    try {
      const recognition = new SpeechRecognition();
      recognition.continuous = false;
      recognition.interimResults = false;
      recognition.lang = 'en-US';

      recognition.onstart = () => {
        setIsListeningVoice(true);
        setVoiceFeedbackMsg("🎙️ Listening... Speak a key phrase clearly (e.g. 'wash hands', 'knock on door', 'introduce').");
      };

      recognition.onresult = (event: any) => {
        const transcript = event.results[0][0].transcript.toLowerCase();
        setVoiceFeedbackMsg(`🗣️ Heard: "${transcript}"`);

        // Fuzzy match search on available steps
        const matchedStep = practiceAvailableSteps.find(step => {
          const text = step.text.toLowerCase();
          const title = step.title.toLowerCase();
          const words = transcript.split(/\s+/).filter((w: string) => w.length > 2);
          if (words.length === 0) return text.includes(transcript) || title.includes(transcript);

          const matches = words.filter((w: string) => text.includes(w) || title.includes(w));
          // match density requirement: 2 words or 50% whichever is smaller
          return matches.length >= Math.min(2, words.length);
        });

        if (matchedStep) {
          addStepToSelection(matchedStep);
          setVoiceFeedbackMsg(`✅ Matched & Placed: "${matchedStep.text.substring(0, 50)}..."`);
        } else {
          setVoiceFeedbackMsg(`❓ No matching step found. Try again using specific keyword words (e.g., 'soap', 'water').`);
        }
      };

      recognition.onerror = (e: any) => {
        console.warn("Speech error:", e);
        setVoiceFeedbackMsg(`❌ Voice Error: ${e.error || "Unable to capture microphone"}`);
        setIsListeningVoice(false);
      };

      recognition.onend = () => {
        setIsListeningVoice(false);
      };

      recognitionRef.current = recognition;
      recognition.start();
    } catch (err: any) {
      setVoiceFeedbackMsg(`❌ Initialization failed: ${err.message}`);
      setIsListeningVoice(false);
    }
  };

  const stopVoiceListening = () => {
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (e) {}
      setIsListeningVoice(false);
    }
  };

  const isTipAddressed = (keywords: string[]) => {
    return practiceSelectedSteps.some(step => {
      const textLower = step.text.toLowerCase();
      const titleLower = step.title.toLowerCase();
      return keywords.some(kw => textLower.includes(kw) || titleLower.includes(kw));
    });
  };

  const finishSkill = () => {
    if (!selectedSkill) return;
    if (!completedSkills.includes(selectedSkill.id)) {
        const newCompleted = [...completedSkills, selectedSkill.id];
        setCompletedSkills(newCompleted);
        localStorage.setItem(`${certPrefix}_completed_skills`, JSON.stringify(newCompleted));
        GamificationService.addPoints(50);
        triggerSkillMasteredToast(selectedSkill.name);
        if (window.trackEvent) window.trackEvent('skill_procedure_completed', 'skill_lab', selectedSkill.name);
    }
    handleClose();
  };

  const handleNextSlide = useCallback(() => {
    if (!selectedSkill) return;

    const nextIndex = currentSlideIndex + 1;
    const interval = config.intervals.skills_lab || 4;

    if (user?.planTier === 'free' && config.isActive && config.intermissionEnabled && nextIndex > 0 && nextIndex % interval === 0 && lastAdIndex !== nextIndex) {
        setShowAdBreak(true);
        setLastAdIndex(nextIndex);
        return;
    }

    if (currentSlideIndex < selectedSkill.slides.length - 1) {
      setCurrentSlideIndex(nextIndex);
      setShowHint(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      if (selectedSkill.quiz && selectedSkill.quiz.length > 0) {
          setShowQuiz(true);
          setQuizIndex(0);
          setQuizScore(0);
          setIsQuizCompleted(false);
          setSelectedOption(null);
          setIsAnswered(false);
          setShowHint(false);
      } else {
          finishSkill();
      }
    }
  }, [selectedSkill, currentSlideIndex, completedSkills, user?.planTier, config, lastAdIndex, certPrefix]);

  const handlePrevSlide = useCallback(() => {
    if (currentSlideIndex > 0) {
      setCurrentSlideIndex(prev => prev - 1);
      setShowHint(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentSlideIndex]);

  const handlePrint = () => {
    setTimeout(() => {
        window.print();
        if (window.trackEvent && selectedSkill) window.trackEvent('skill_procedure_printed', 'skill_lab', selectedSkill.name);
    }, 100);
  };

  const handleSpeakText = (textToSpeak: string) => {
    if ('speechSynthesis' in window) {
      if (isSpeaking) {
        window.speechSynthesis.cancel();
        setIsSpeaking(false);
        return;
      }
      const utterance = new SpeechSynthesisUtterance(textToSpeak.replace(/[“""”]/g, ''));
      utterance.rate = speechSpeed || 1.0;
      utterance.onend = () => setIsSpeaking(false);
      utterance.onerror = () => setIsSpeaking(false);
      setIsSpeaking(true);
      window.speechSynthesis.speak(utterance);
    }
  };

  const onTouchStart = (e: React.TouchEvent) => {
    touchStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  };

  const onTouchEnd = (e: React.TouchEvent) => {
    if (!touchStart.current) return;
    const deltaX = e.changedTouches[0].clientX - touchStart.current.x;
    const deltaY = e.changedTouches[0].clientY - touchStart.current.y;
    touchStart.current = null;
    if (Math.abs(deltaX) > 50 && Math.abs(deltaX) > Math.abs(deltaY)) {
      if (deltaX > 0) handlePrevSlide();
      else handleNextSlide();
    }
  };

  const handleQuizAnswer = (optionId: string, isCorrect: boolean) => {
      if (isAnswered) return;
      setSelectedOption(optionId);
      setIsAnswered(true);
      if (isCorrect) {
          setQuizScore(prev => prev + 1);
          GamificationService.addPoints(10);
      }
  };

  const handleNextQuiz = () => {
      if (!selectedSkill?.quiz) return;
      if (quizIndex < selectedSkill.quiz.length - 1) {
          setQuizIndex(prev => prev + 1);
          setIsAnswered(false);
          setSelectedOption(null);
      } else {
          setIsQuizCompleted(true);
      }
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!selectedSkill || showAdBreak) return;
      if (['INPUT', 'TEXTAREA'].includes((e.target as HTMLElement).tagName)) return;
      if (showQuiz) return; 
      if (e.key === 'ArrowRight') handleNextSlide();
      else if (e.key === 'ArrowLeft') handlePrevSlide();
      else if (e.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedSkill, handleNextSlide, handlePrevSlide, showQuiz, showAdBreak]);

  // Derived filter list of categories mapped
  const filteredSkills = useMemo(() => {
    return skills.filter(skill => {
      const matchesSearch = skill.name.toLowerCase().includes(searchTerm.toLowerCase());
      if (activeCategoryTab === 'all') return matchesSearch;
      const cat = getSkillCategory(skill).name;
      return matchesSearch && cat === activeCategoryTab;
    });
  }, [skills, searchTerm, activeCategoryTab]);

  const uniqueCategories = useMemo(() => {
    const tempSet = new Set(skills.map(s => getSkillCategory(s).name));
    return Array.from(tempSet).sort();
  }, [skills]);

  if (isLoading) {
      return <PageLoader message="Loading Clinical Skills..." text="Compiling procedure catalog..." />;
  }

  if (isFetchingFullSkill) {
      return <PageLoader message="Loading Clinical Skill Details..." text="Loading steps and checklists..." />;
  }

  if (showAdBreak) {
      return <AdIntervalBreak moduleName="Skills Lab" onComplete={() => setShowAdBreak(false)} />;
  }

  return (
    <div className={selectedSkill ? "w-full animate-fade-in text-slate-900 dark:text-slate-50 pb-20" : "max-w-7xl mx-auto pb-32 animate-fade-in text-slate-900 dark:text-slate-50"}>
      {/* Print-Only Formatted Layout */}
      <PrintStudyDocument
        modules={STUDY_MODULES}
        skills={skills}
        selectedSkill={selectedSkill}
        scope={printScope}
        certName={activeCert?.name || "Nurse Aide Certification (CNA)"}
      />

      {/* Export & Print Options Modal */}
      <PrintStudyGuideModal
        isOpen={isPrintModalOpen}
        onClose={() => setIsPrintModalOpen(false)}
        onConfirmPrint={handleConfirmPrint}
        onConfirmDownloadPdf={handleConfirmDownloadPdf}
        isGeneratingPdf={isGeneratingPdf}
        currentTitle={selectedSkill?.name}
        isCurrentSkillActive={!!selectedSkill}
        totalModulesCount={STUDY_MODULES.length}
        totalSkillsCount={skills.length}
      />

      {expandedImage && (
        <div className="fixed inset-0 z-[200] bg-slate-950/95 backdrop-blur-xl flex items-center justify-center p-4 md:p-12 animate-pop no-print" onClick={() => setExpandedImage(null)}>
            <button className="absolute top-8 right-8 p-4 bg-white/10 text-white rounded-full hover:bg-white/20 transition-all"><X size={24}/></button>
            <img src={expandedImage} className="max-w-full max-h-full rounded-3xl shadow-2xl border border-white/10 object-contain" alt="Expanded Visual" />
        </div>
      )}

      {!selectedSkill ? (
        /* Page 1: Skills List - Visual Theme: Structured Course-Platform */
        <div className="no-print space-y-8">
          <AdUnit placement="skills_lab" />
          {/* DYNAMIC PROGRESSIVE DASHBOARD PROGRESS WIDGET */}
          <div className={`p-6 md:p-8 text-white shadow-2xl relative overflow-hidden border rounded-[2.5rem] ${
            tokens.isWarmAcademic
              ? 'bg-[#0D6E6E] border-[#0B5C5C]'
              : tokens.isClinical
              ? 'bg-gradient-to-br from-[#0B2942] via-[#0D3C5C] to-[#071F33] border-teal-500/20'
              : tokens.isHighContrast
              ? 'bg-black text-amber-400 border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]'
              : tokens.isMinimal
              ? 'bg-slate-900 border-slate-800'
              : tokens.isVibrant
              ? 'bg-gradient-to-br from-violet-900 via-indigo-900 to-slate-950 border-violet-800'
              : 'bg-slate-900 border-white/5'
          }`}>
            <div className="absolute top-0 right-0 w-80 h-80 bg-emerald-500/10 blur-[80px] pointer-events-none -translate-y-1/2 translate-x-1/2"></div>
            <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
              
              <div className="lg:col-span-4 space-y-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`px-2.5 py-0.5 text-[8px] font-black rounded border uppercase tracking-widest flex items-center gap-1 ${
                    tokens.isClinical ? 'bg-teal-500/20 text-teal-300 border-teal-500/30' : tokens.isHighContrast ? 'bg-amber-300 text-black border-black' : tokens.isVibrant ? 'bg-violet-500/20 text-violet-300 border-violet-500/30' : 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30'
                  }`}>
                    <ShieldCheck size={10} className="animate-pulse" /> Exam Prep
                  </span>
                  <span className="px-2.5 py-0.5 bg-indigo-500/20 text-indigo-300 text-[8px] font-black rounded border border-indigo-500/30 uppercase tracking-widest font-extrabold pb-0.5">
                    CNA Skills
                  </span>
                </div>
                <h1 className={`text-3xl font-black tracking-tight text-white leading-none ${tokens.isClinical ? 'font-serif' : ''}`}>Skills Lab</h1>
                <p className="text-xs text-slate-350 leading-relaxed font-semibold">
                  Learn and practice your clinical care skills for the exam. Master the steps, supplies, and safety rules.
                </p>
                <div className="pt-2 select-none">
                  <button 
                    onClick={() => {
                      setPrintScope(selectedSkill ? 'current' : 'skills_lab');
                      setIsPrintModalOpen(true);
                    }}
                    className="px-4 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest transition-all flex items-center gap-2 border border-emerald-400 shadow-lg active:scale-95 cursor-pointer"
                    id="print-skills-lab-and-guide-btn"
                    title="Print formatted clinical skills manual and study guide PDF layout"
                  >
                    <Printer size={15} />
                    <span>Print Study Guide & Skill Lab</span>
                  </button>
                </div>
              </div>

              {/* Central Column Search Input */}
              <div className="lg:col-span-4 lg:px-4 py-1">
                <label className="text-[8px] font-black tracking-widest text-[#a7f3d0] uppercase mb-2 block font-extrabold">Search Filters</label>
                <div className="relative group w-full">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-emerald-400 transition-colors" size={16} />
                  <input 
                    type="text" 
                    placeholder="Search skills (e.g., wash hands)..." 
                    value={searchTerm} 
                    onChange={e => setSearchTerm(e.target.value)} 
                    className="w-full pl-11 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl outline-none text-white placeholder:text-slate-500 font-bold text-xs ring-offset-slate-900 focus:ring-2 focus:ring-emerald-500 focus:bg-slate-950 transition-all shadow-inner" 
                  />
                  {searchTerm && (
                    <button onClick={() => setSearchTerm('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-455 hover:text-white">
                      <X size={14} />
                    </button>
                  )}
                </div>
              </div>

              {/* Metric display progress stats card */}
              <div className="lg:col-span-4 bg-white/5 rounded-2xl p-5 border border-white/10 flex items-center justify-between gap-4">
                <div className="space-y-1 flex-1 min-w-0">
                  <div className="flex justify-between items-end">
                    <span className="text-[8px] font-black tracking-widest text-[#a7f3d0]">SKILLS PASSED</span>
                    <span className="text-xs font-black text-white">{completedSkills.length}/{skills.length}</span>
                  </div>
                  <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden mt-1.5">
                    <div 
                      className="h-full bg-gradient-to-r from-emerald-500 to-teal-400 transition-all duration-1000" 
                      style={{ width: `${(completedSkills.length / Math.max(1, skills.length)) * 100}%` }}
                    />
                  </div>
                  <p className="text-[8px] font-black text-slate-400 mt-1 uppercase">
                    {Math.round((completedSkills.length / Math.max(1, skills.length)) * 100)}% of skills completed
                  </p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-emerald-500/20 text-emerald-400 flex items-center justify-center shrink-0 border border-emerald-500/20 shadow-inner">
                  <Trophy size={20} className="animate-bounce" />
                </div>
              </div>

            </div>
          </div>

          {/* SINGLE CARD-BASED CATEGORY FILTERING MECHANISM */}
          <div className="space-y-3 pt-2 font-sans">
            <div className="flex justify-between items-center py-1">
              <h2 className="text-[10px] font-black tracking-widest uppercase text-slate-400 dark:text-slate-400 flex items-center gap-1.5 leading-none">
                <Layers size={13} className="text-emerald-500" /> Categories ({uniqueCategories.length})
              </h2>
              <span className="text-[8.5px] font-bold text-slate-400 leading-none">Click a card to filter skills below</span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
              {/* All Skills Filter Card */}
              <button
                onClick={() => setActiveCategoryTab('all')}
                className={`p-4 rounded-2xl text-left flex flex-col justify-between h-28 transition-all hover:-translate-y-0.5 border ${
                  activeCategoryTab === 'all'
                    ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-950 border-slate-900 dark:border-white shadow-lg ring-2 ring-emerald-500/20'
                    : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 hover:border-emerald-400 dark:hover:border-emerald-700'
                }`}
              >
                <div className="flex justify-between items-start w-full">
                  <div className={`p-2 rounded-xl ${activeCategoryTab === 'all' ? 'bg-emerald-500 text-white' : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300'}`}>
                    <ListChecks size={14} />
                  </div>
                  <span className={`text-[8px] font-extrabold tracking-wider ${activeCategoryTab === 'all' ? 'text-emerald-300 dark:text-emerald-700' : 'text-slate-400'}`}>
                    {completedSkills.length}/{skills.length} OK
                  </span>
                </div>
                <div>
                  <h3 className="text-xs font-black leading-tight">All Skills</h3>
                  <div className="w-full h-1 bg-slate-200 dark:bg-slate-800 rounded-full mt-2 overflow-hidden">
                    <div className="h-full bg-emerald-500" style={{ width: `${(completedSkills.length / Math.max(1, skills.length)) * 100}%` }} />
                  </div>
                </div>
              </button>

              {/* 6 Category Filter Cards */}
              {uniqueCategories.map(catName => {
                const skillsInCat = skills.filter(s => getSkillCategory(s).name === catName);
                const catConfig = getSkillCategory(skillsInCat[0]);
                const CategoryIcon = catConfig.icon;
                const progressCount = skillsInCat.filter(s => completedSkills.includes(s.id)).length;
                const isActive = activeCategoryTab === catName;

                return (
                  <button
                    key={catName}
                    onClick={() => setActiveCategoryTab(isActive ? 'all' : catName)}
                    className={`p-4 rounded-2xl text-left flex flex-col justify-between h-28 transition-all hover:-translate-y-0.5 border ${
                      isActive
                        ? 'bg-emerald-600 text-white border-emerald-600 shadow-lg ring-2 ring-emerald-500/30'
                        : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800/80 hover:border-emerald-400 dark:hover:border-emerald-700'
                    }`}
                  >
                    <div className="flex justify-between items-start w-full">
                      <div className={`p-2 rounded-xl ${isActive ? 'bg-white/20 text-white' : catConfig.badgeColor}`}>
                        <CategoryIcon size={14} />
                      </div>
                      <span className={`text-[8px] font-extrabold tracking-wider ${isActive ? 'text-emerald-100' : 'text-slate-400'}`}>
                        {progressCount}/{skillsInCat.length} OK
                      </span>
                    </div>
                    <div>
                      <h3 className={`text-[10px] font-black leading-tight line-clamp-2 ${isActive ? 'text-white' : 'text-slate-950 dark:text-slate-100'}`}>
                        {catName}
                      </h3>
                      <div className={`w-full h-1 rounded-full mt-2 overflow-hidden ${isActive ? 'bg-white/30' : 'bg-slate-100 dark:bg-slate-950'}`}>
                        <div className={isActive ? 'h-full bg-white' : 'h-full bg-emerald-500'} style={{ width: `${(progressCount / Math.max(1, skillsInCat.length)) * 100}%` }} />
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* GRID OF INDIVIDUAL PROCEDURES */}
          <div className="space-y-4">
            <div className="flex justify-between items-center py-1">
              <h3 className="text-[10px] font-extrabold tracking-widest text-slate-400 uppercase flex items-center gap-1.5">
                <ClipboardList size={13} className="text-emerald-500" /> Skills List
              </h3>
              <p className="text-[8.5px] text-slate-400 font-bold leading-none">Click any skill to start studying</p>
            </div>

            {filteredSkills.length === 0 ? (
               <div className="bg-slate-50 dark:bg-slate-900 p-16 rounded-[2rem] border-2 border-dashed border-slate-200 dark:border-slate-800 text-center space-y-3 opacity-80">
                  <Eye size={40} className="mx-auto text-slate-400" />
                  <p className="text-xs font-black text-slate-500 uppercase tracking-widest">No skills found</p>
                  <button onClick={() => { setSearchTerm(''); setActiveCategoryTab('all'); }} className="px-5 py-2 bg-slate-900 text-white rounded-lg text-[9px] font-black tracking-widest uppercase shadow">Reset Filters</button>
               </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredSkills.map((skill) => {
                  const Icon = SkillIconResolver(skill.icon);
                  const isDone = completedSkills.includes(skill.id);
                  const catInfo = getSkillCategory(skill);
                  const CatIcon = catInfo.icon;
                  
                  const hasCriticals = (skill.slides || []).some(s => s.type === 'criticals' || s.data?.criticalPoints);
                  const hasVideo = (skill.slides || []).some(s => s.type === 'embedded' || s.data?.embedUrl || s.data?.embedCode || s.data?.videoUrl || s.data?.youtubeUrl) || !!skill.videoUrl || !!(skill as any).hasVideo;
                  const procedureStepsCount = (skill.slides || []).filter(s => s.type === 'procedure').length || skill.slides?.length || 0;

                  return (
                    <div 
                      key={skill.id}
                      onClick={() => handleOpenSkill(skill, 'study')}
                      className={`group rounded-[2rem] border hover:border-emerald-500/50 dark:hover:border-emerald-500/40 hover:shadow-xl transition-all duration-300 text-left cursor-pointer flex flex-col justify-between p-6 relative overflow-hidden active:scale-[0.99] ${
                        config?.enableVibrantCards !== false 
                          ? 'bg-gradient-to-br from-emerald-50/70 via-emerald-50/30 to-emerald-100/10 border-emerald-200/80 dark:from-slate-900/90 dark:via-emerald-950/35 dark:to-emerald-950/55 dark:border-emerald-800/60 shadow-[0_4px_20px_-4px_rgba(16,185,129,0.08)]'
                          : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800/80'
                      }`}
                    >
                      {/* Interactive glow border top */}
                      <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-emerald-500 to-teal-400 opacity-60" />

                      <div>
                        <div className="flex justify-between items-start w-full gap-4 mb-3">
                          <div className="p-3 bg-slate-50 dark:bg-slate-950 text-slate-800 dark:text-slate-200 border border-slate-200 dark:border-slate-850 rounded-2xl flex items-center justify-center shrink-0 shadow-xs transition-transform group-hover:scale-105 group-hover:bg-emerald-500 group-hover:text-white dark:group-hover:bg-emerald-600">
                             <Icon size={20} className="transition-all" />
                          </div>

                          <div className="flex items-center gap-1.5 shrink-0 flex-wrap justify-end">
                            {hasVideo && (
                              <span className="px-2 py-0.5 bg-indigo-600 text-white text-[8px] font-black rounded-lg border border-indigo-700 flex items-center gap-1 uppercase tracking-wider shadow-xs" title="Clinical Video Demonstration Included">
                                <Video size={10} className="shrink-0 text-white" /> Video
                              </span>
                            )}
                            {hasCriticals && (
                              <span className="px-2 py-0.5 bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400 text-[7.5px] font-black rounded-lg border border-amber-200 dark:border-amber-900/50 flex items-center gap-1 uppercase tracking-wider">
                                <AlertTriangle size={9} className="shrink-0 text-amber-600 dark:text-amber-400" /> Critical Steps
                              </span>
                            )}
                            {hasScenarioForSkill(skill) && (
                              <span className="px-2 py-0.5 bg-blue-50 dark:bg-blue-950/50 text-blue-700 dark:text-blue-300 text-[7.5px] font-black rounded-lg border border-blue-200 dark:border-blue-800 flex items-center gap-1 uppercase tracking-wider">
                                <Activity size={9} className="text-blue-500" /> 360° VR
                              </span>
                            )}
                            {isDone ? (
                              <span className="px-2.5 py-1 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 text-[7px] font-black rounded-lg border border-emerald-100 dark:border-emerald-900/50 flex items-center gap-1 shadow-xs uppercase tracking-wider">
                                <CheckCircle size={10} fill="currentColor" className="text-white dark:text-emerald-950" /> Done ✦
                              </span>
                            ) : (
                              <span className="px-2.5 py-1 bg-slate-50 dark:bg-slate-950 text-slate-455 dark:text-slate-500 text-[7px] font-black rounded-lg border border-slate-200 dark:border-slate-850 uppercase tracking-widest leading-none">
                                Not Started
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Category & Title block */}
                        <div>
                          <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[8px] font-black uppercase tracking-wider w-fit border mb-1.5 ${catInfo.badgeColor}`}>
                            <CatIcon size={9} />
                            {catInfo.name}
                          </span>
                          <h3 className="text-sm md:text-base font-black text-slate-955 dark:text-slate-50 leading-snug tracking-tight line-clamp-2 group-hover:text-emerald-655 dark:group-hover:text-emerald-400 transition-colors mb-2 flex items-center justify-between gap-2">
                            <span className="truncate">{skill.name}</span>
                            {hasVideo && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-indigo-600 text-white text-[8.5px] font-black border border-indigo-700 shadow-xs shrink-0" title="Clinical Demonstration Video Included">
                                <PlayCircle size={11} className="shrink-0 text-white" />
                                <span>Video</span>
                              </span>
                            )}
                          </h3>
                        </div>

                        {/* Metadata row */}
                        <div className="flex items-center gap-3 text-slate-400 text-[10px] font-extrabold uppercase tracking-wide mb-4 flex-wrap">
                          <div className="flex items-center gap-1.5">
                            <ClipboardList size={12} className="text-slate-500" />
                            <span>{procedureStepsCount} Procedural Steps</span>
                            {hasVideo && (
                              <span 
                                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-indigo-50 dark:bg-indigo-950/80 text-indigo-700 dark:text-indigo-300 font-black text-[8px] border border-indigo-200 dark:border-indigo-800 shrink-0 ml-0.5" 
                                title="Video demonstration attached to this procedural skill"
                              >
                                <PlayCircle size={10} className="shrink-0 text-indigo-600 dark:text-indigo-400" />
                                <span>Video</span>
                              </span>
                            )}
                          </div>
                          {hasVideo && (
                            <>
                              <span>•</span>
                              <div className="flex items-center gap-1 text-indigo-600 dark:text-indigo-400 font-extrabold">
                                <Video size={12} className="text-indigo-500 shrink-0" />
                                <span>Video Demo</span>
                              </div>
                            </>
                          )}
                          <span>•</span>
                          <div className="flex items-center gap-1">
                            <Clock size={11} className="text-slate-500" />
                            <span>~6–8 min</span>
                          </div>
                        </div>
                      </div>

                      {/* Action buttons: Study, Test & Simulate */}
                      <div className="pt-3 border-t border-slate-100 dark:border-slate-800/80 w-full flex items-center gap-2">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenSkill(skill, 'study');
                          }}
                          className="flex-1 py-2 px-2.5 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-800 dark:text-slate-200 rounded-xl text-[9px] font-black tracking-wider uppercase transition-all flex items-center justify-center gap-1 border border-slate-200 dark:border-slate-700"
                        >
                          <BookOpen size={12} className="text-emerald-600 dark:text-emerald-400" />
                          <span>Study</span>
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenSkill(skill, 'test');
                          }}
                          className="flex-1 py-2 px-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-[9px] font-black tracking-wider uppercase transition-all flex items-center justify-center gap-1 shadow-xs active:scale-95"
                        >
                          <Zap size={12} />
                          <span>Test</span>
                        </button>
                        {isClinicalSimulatorActive && hasScenarioForSkill(skill) && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenSkill(skill, 'simulator');
                            }}
                            className="flex-1 py-2 px-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-[9px] font-black tracking-wider uppercase transition-all flex items-center justify-center gap-1 shadow-xs active:scale-95"
                            title="Launch 360° VR Clinical Simulator"
                          >
                            <Activity size={12} className="text-white" />
                            <span>360° Sim</span>
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      ) : showQuiz ? (
        <div className="animate-pop max-w-4xl mx-auto space-y-8 no-print pb-24">
            {!isQuizCompleted ? (
                <div className="space-y-8">
                    <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] p-8 md:p-10 border border-slate-100 dark:border-slate-800 shadow-xl overflow-hidden relative group">
                        <div className="absolute top-0 right-0 p-8 opacity-[0.03] group-hover:rotate-12 transition-transform duration-700 hidden md:block"><HelpCircle size={160}/></div>
                        <div className="flex items-center justify-between mb-8">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-indigo-50 dark:bg-indigo-950 text-indigo-650 dark:text-indigo-400 rounded-xl shadow-inner"><Brain size={18}/></div>
                                <h3 className="text-[9px] font-black tracking-widest text-slate-400 uppercase">Quiz Question {quizIndex + 1}/{selectedSkill.quiz?.length}</h3>
                            </div>
                            <button onClick={handleClose} className="flex items-center gap-2 px-3 py-2 bg-slate-100 hover:bg-slate-200 dark:bg-slate-950 dark:hover:bg-slate-850 text-slate-600 dark:text-slate-300 rounded-lg transition-all active:scale-95 group border dark:border-slate-800">
                                <ChevronLeft size={16} strokeWidth={3} className="group-hover:-translate-x-0.5 transition-transform" />
                                <span className="text-[8.5px] font-black tracking-widest hidden sm:inline uppercase">Quit</span>
                            </button>
                        </div>
                        <h2 className="text-xl md:text-2xl font-black text-slate-900 dark:text-white leading-snug mb-10">"{selectedSkill.quiz![quizIndex].text}"</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {selectedSkill.quiz![quizIndex].options.map((opt, i) => {
                                const isThisSelected = selectedOption === opt._id;
                                let style = "border-slate-100 dark:border-slate-800/80 bg-slate-50/50 dark:bg-slate-950/40 hover:border-indigo-305 hover:bg-white";
                                if (isAnswered) {
                                    if (opt.isCorrect) style = "border-emerald-500 bg-emerald-50 dark:bg-emerald-950/20 text-emerald-900 dark:text-emerald-300 shadow-lg ring-4 ring-emerald-500/10";
                                    else if (isThisSelected) style = "border-rose-500 bg-rose-50 dark:bg-rose-950/20 text-rose-900 dark:text-rose-305";
                                    else style = "opacity-40 grayscale border-transparent bg-slate-50 dark:bg-slate-950";
                                }
                                return (
                                    <button 
                                        key={opt._id}
                                        onClick={() => handleQuizAnswer(opt._id, !!opt.isCorrect)}
                                        disabled={isAnswered}
                                        className={`w-full p-6 rounded-2xl border-2 text-left font-extrabold flex items-center gap-5 transition-all ${style}`}
                                    >
                                        <div className={`w-10 h-10 rounded-xl border-2 flex items-center justify-center shrink-0 font-extrabold text-xs ${isThisSelected ? 'bg-indigo-600 text-white border-indigo-605' : 'bg-white dark:bg-slate-900 border-slate-205 dark:border-slate-800 text-slate-400'}`}>{String.fromCharCode(65+i)}</div>
                                        <span className="text-sm md:text-base leading-tight">{opt.text}</span>
                                    </button>
                                );
                            })}
                        </div>
                        {isAnswered && (
                            <div className="mt-10 p-6 md:p-8 bg-indigo-50 dark:bg-indigo-950/20 border-2 border-indigo-100 dark:border-indigo-900 rounded-3xl animate-slide-up relative overflow-hidden">
                                <h4 className="text-[9px] font-black text-indigo-500 mb-3 tracking-widest uppercase flex items-center gap-2"><Info size={14}/> Why this is correct</h4>
                                <p className="text-sm md:text-base font-bold text-indigo-950 dark:text-indigo-200 leading-relaxed">"{selectedSkill.quiz![quizIndex].explanation}"</p>
                            </div>
                        )}
                    </div>
                    {isAnswered && (
                        <div className="flex justify-end">
                            <button 
                                onClick={handleNextQuiz}
                                className="px-12 py-5 bg-slate-900 dark:bg-white dark:text-slate-950 text-white rounded-2xl font-black text-xs tracking-widest shadow-2xl hover:bg-black dark:hover:bg-slate-100 transition-all active:scale-95 flex items-center justify-center gap-3 border-b-4 border-slate-950 dark:border-slate-200"
                            >
                                {quizIndex === selectedSkill.quiz!.length - 1 ? 'Finish Quiz' : 'Next Question'} <ArrowRight size={18} />
                            </button>
                        </div>
                    )}
                </div>
            ) : (
                <div className="space-y-8">
                  <div className="bg-slate-900 dark:bg-slate-955 rounded-[3rem] p-12 md:p-20 text-white text-center shadow-2xl relative overflow-hidden group border border-white/5">
                      <div className="relative z-10 space-y-8">
                          <div className="w-20 h-20 bg-emerald-500 rounded-3xl flex items-center justify-center mx-auto shadow-2xl rotate-3">
                              <Trophy size={40} className="text-white" />
                          </div>
                          <div>
                              <h2 className="text-4xl md:text-5xl font-black tracking-tighter leading-none mb-2">Skill Completed!</h2>
                              <p className="text-slate-455 text-[10px] font-black tracking-[0.3em] uppercase">Well Done!</p>
                          </div>
                          <div className="flex justify-center gap-12 py-6">
                              <div className="text-center">
                                  <p className="text-5xl font-black">{Math.round((quizScore / selectedSkill.quiz!.length) * 100)}%</p>
                                  <p className="text-[9px] font-black text-slate-550 mt-1 tracking-widest uppercase">Quiz Score</p>
                              </div>
                          </div>
                          <button onClick={finishSkill} className="px-14 py-5 bg-white text-slate-950 rounded-2xl font-black text-[10px] tracking-widest uppercase border-b-4 border-slate-300">Go Back to Skills</button>
                      </div>
                  </div>

                  <div className="p-6 md:p-8 bg-white dark:bg-slate-950 rounded-[2.5rem] border border-slate-150 dark:border-slate-850 shadow-xl">
                      <AdvanceSuggestionPanel 
                        source="skills" 
                        score={Math.round((quizScore / selectedSkill.quiz!.length) * 100)} 
                        moduleId={selectedSkill.id}
                        moduleTitle={selectedSkill.name}
                      />
                  </div>
                </div>
            )}
        </div>
      ) : (
        <article className="animate-pop w-full mx-auto flex flex-col no-print" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
          <AdUnit placement="skills_lab_top" className="mb-6" />
          
          {/* TOP CONTROLS STICKY DOCK BAR */}
          <div className="bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl p-3.5 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xl flex justify-between items-center sticky top-2.5 z-50 mb-6 transition-all">
            <div className="flex items-center gap-3 overflow-hidden">
              <button 
                onClick={handleClose} 
                className="flex items-center gap-1 px-4 py-2 bg-gradient-to-r from-rose-500 to-rose-600 hover:from-rose-600 hover:to-rose-700 text-white font-extrabold text-[10px] tracking-widest uppercase rounded-xl transition-all shadow-[0_4px_12px_rgba(244,63,94,0.3)] active:scale-95 group shrink-0 border border-rose-400/20"
                aria-label="Exit skill simulation course"
              >
                <ChevronLeft size={16} strokeWidth={3} className="group-hover:-translate-x-0.5 transition-transform" />
                <span className="hidden lg:inline">Go Back</span>
              </button>
              <div className="min-w-0 pr-4">
                <h1 className="text-xs md:text-sm font-black text-slate-900 dark:text-white truncate max-w-[155px] md:max-w-md mb-1 leading-none uppercase flex items-center gap-1.5">
                  <span className="truncate">{selectedSkill.name}</span>
                  {(selectedSkill.slides || []).some(s => s.type === 'embedded' || s.data?.embedUrl || s.data?.embedCode || s.data?.videoUrl || s.data?.youtubeUrl) && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-indigo-50 dark:bg-indigo-950/70 text-indigo-600 dark:text-indigo-400 text-[8px] font-black border border-indigo-200/80 dark:border-indigo-800 shrink-0" title="Clinical Demonstration Video Available">
                      <PlayCircle size={9} className="shrink-0" />
                      <span>Video</span>
                    </span>
                  )}
                </h1>
                <div className="flex items-center gap-2">
                  <div className="flex gap-0.5 shrink-0">
                    {selectedSkill.slides.map((_, i) => (
                      <div key={i} className={`h-1.5 rounded-full transition-all ${i === currentSlideIndex ? 'w-5 bg-emerald-500' : 'w-1 bg-slate-200 dark:bg-slate-800'}`} />
                    ))}
                  </div>
                  <span className="text-[7.5px] font-black text-slate-400 tracking-widest uppercase shrink-0">{currentSlideIndex + 1}/{selectedSkill.slides.length} STEPS</span>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <button 
                onClick={() => {
                  setPrintScope('current');
                  setIsPrintModalOpen(true);
                }}
                className="hidden md:flex p-2.5 bg-emerald-50 hover:bg-emerald-100 dark:bg-emerald-950/20 text-emerald-600 dark:text-emerald-400 rounded-lg border border-emerald-100 dark:border-emerald-900/50 items-center gap-2 transition-all active:scale-[0.93] cursor-pointer"
                title="Print current skill procedure or full study guide and skills manual"
                id="print-skill-procedure-btn"
              >
                <Printer size={16} />
                <span className="text-[8.5px] font-black tracking-widest uppercase">Print Study Guide & Skill</span>
              </button>
              <button onClick={() => setShowHint(!showHint)} className={`p-2.5 rounded-lg border transition-all active:scale-[0.93] ${showHint ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-slate-50 dark:bg-slate-950 text-slate-500 border-slate-200 dark:border-slate-800 hover:bg-slate-100'}`} title="Instructor Hint">
                <Brain size={16} />
              </button>
              <div className="w-px h-6 bg-slate-200 dark:bg-slate-800 mx-1"></div>
              <div className="flex gap-1.5">
                <button onClick={handlePrevSlide} disabled={currentSlideIndex === 0} className="p-2.5 bg-white dark:bg-slate-900 hover:bg-slate-50 text-slate-600 dark:text-slate-400 rounded-lg border border-slate-200 dark:border-slate-800 disabled:opacity-20 transition-all"><ChevronLeft size={18} /></button>
                <button onClick={handleNextSlide} className="px-6 py-2.5 rounded-lg font-black text-[9px] tracking-[0.2em] bg-slate-900 dark:bg-white dark:text-slate-900 text-white hover:bg-black dark:hover:bg-slate-100 transition-all active:scale-95 border-b-2 border-black dark:border-slate-200 uppercase">
                  {currentSlideIndex === selectedSkill.slides.length - 1 ? (selectedSkill.quiz?.length ? 'Take Quiz' : 'Finish') : 'Next Step'}
                </button>
              </div>
            </div>
          </div>

          {/* Mode Selector + View Switcher */}
          {config?.isSkillsPracticeModeEnabled !== false && (
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
              {/* Study Mode vs Test Mode vs Clinical Simulator */}
              <div className="flex flex-wrap items-center bg-slate-100 dark:bg-slate-950 rounded-2xl p-1 sm:p-1.5 border border-slate-200/60 dark:border-slate-800 w-full sm:w-auto gap-1 sm:gap-1.5">
                <button 
                  id="tab-study-mode-btn"
                  onClick={() => {
                    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
                      window.speechSynthesis.cancel();
                    }
                    setIsSpeaking(false);
                    setAutoStartTourOnLaunch(false);
                    setPracticeMode(false);
                    setSimulatorMode(false);
                  }}
                  className={`px-3 sm:px-4 py-2 sm:py-2.5 rounded-xl text-[9px] sm:text-[10px] font-black tracking-widest uppercase transition-all flex items-center justify-center gap-1.5 ${!practiceMode && !simulatorMode ? 'bg-white dark:bg-slate-900 text-slate-950 dark:text-white shadow-xs border border-slate-100 dark:border-slate-800' : 'text-slate-505 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-205'}`}
                >
                  <BookOpen size={13} />
                  Study Mode
                </button>
                <button 
                  id="tab-test-mode-btn"
                  onClick={() => {
                    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
                      window.speechSynthesis.cancel();
                    }
                    setIsSpeaking(false);
                    setAutoStartTourOnLaunch(false);
                    setSimulatorMode(false);
                    initiatePracticeMode();
                  }}
                  className={`px-3 sm:px-4 py-2 sm:py-2.5 rounded-xl text-[9px] sm:text-[10px] font-black tracking-widest uppercase transition-all flex items-center justify-center gap-1.5 ${practiceMode && !simulatorMode ? 'bg-emerald-600 dark:bg-emerald-600 text-white shadow-xs' : 'text-slate-555 dark:text-slate-405 hover:text-slate-800 dark:hover:text-slate-200'}`}
                >
                  <Zap size={13} className={practiceMode && !simulatorMode ? "animate-pulse" : ""} />
                  Test Mode
                </button>
                {isClinicalSimulatorActive && hasScenarioForSkill(selectedSkill) && (
                  <div className="flex flex-wrap items-center gap-1 sm:gap-1.5">
                    {/* Clinical Simulator Free Exploration */}
                    <button 
                      id="tab-clinical-simulator-btn"
                      onClick={() => {
                        if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
                          window.speechSynthesis.cancel();
                        }
                        setIsSpeaking(false);
                        setAutoStartTourOnLaunch(false);
                        initiateSimulatorMode(selectedSkill, false);
                      }}
                      className={`px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-xl text-[9px] sm:text-[10px] font-black tracking-widest uppercase transition-all flex items-center justify-center gap-1.5 ${
                        simulatorMode && !autoStartTourOnLaunch 
                          ? 'bg-blue-600 text-white shadow-xs' 
                          : 'bg-blue-50 dark:bg-blue-950/60 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-900/40'
                      }`}
                      title="Open Clinical Simulator for 360° free exploration"
                    >
                      <Activity size={13} className={simulatorMode && !autoStartTourOnLaunch ? "animate-pulse" : ""} />
                      Clinical Simulator
                      <span className="px-1.5 py-0.5 rounded-md bg-blue-600 text-white text-[8px] font-extrabold uppercase shadow-2xs">
                        360° VR
                      </span>
                    </button>

                    {/* PLAY SKILL TOUR BUTTON */}
                    <button
                      id="skills-lab-play-tour-btn"
                      type="button"
                      onClick={() => {
                        if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
                          window.speechSynthesis.cancel();
                        }
                        setIsSpeaking(false);
                        setAutoStartTourOnLaunch(true);
                        initiateSimulatorMode(selectedSkill, true);
                      }}
                      className={`px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-xl text-[9px] sm:text-[10px] font-black tracking-widest uppercase transition-all flex items-center justify-center gap-1.5 shadow-xs cursor-pointer group active:scale-95 ${
                        simulatorMode && autoStartTourOnLaunch
                          ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white ring-2 ring-emerald-400'
                          : 'bg-emerald-600 hover:bg-emerald-700 text-white'
                      }`}
                      title="Play Skill Tour: Start guided 360° walkthrough with voice narration"
                    >
                      <span className="w-4 h-4 rounded-full bg-white text-emerald-700 flex items-center justify-center shrink-0 shadow-xs group-hover:scale-110 transition-transform">
                        <Play className="w-2.5 h-2.5 fill-current ml-0.5" />
                      </span>
                      <span>Play Skill Tour</span>
                      <span className="px-1.5 py-0.5 rounded bg-black/25 text-white text-[7.5px] font-mono font-bold uppercase tracking-wider">
                        Guided
                      </span>
                    </button>

                    {/* PREVIEW TOUR BUTTON WITH MINI THUMBNAIL & RICH INTERACTIVE VISUAL TOOLTIP */}
                    <div className="relative" ref={skillsLabTourPreviewRef}>
                      <button
                        id="skills-lab-preview-tour-btn"
                        type="button"
                        onClick={() => setShowSkillsLabTourPreview(prev => !prev)}
                        className="px-2 py-1.5 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 hover:border-emerald-400 dark:hover:border-emerald-500 text-slate-800 dark:text-slate-200 shadow-xs flex items-center gap-1.5 transition-all hover:scale-105 active:scale-95 cursor-pointer group"
                        title="Preview Tour: See 360° environment visual snapshot & station itinerary before launching VR"
                        aria-label="Preview Tour visual thumbnail and details"
                      >
                        {/* Mini visual thumbnail */}
                        <div className="relative w-6 h-6 rounded-lg overflow-hidden border border-slate-300 dark:border-slate-700 shrink-0 shadow-2xs group-hover:border-emerald-500 transition-colors">
                          <img
                            src={scenarioPreviewImage}
                            alt="VR 360 preview thumbnail"
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                          />
                          <div className="absolute inset-0 bg-slate-950/25 flex items-center justify-center">
                            <Eye className="w-3 h-3 text-white drop-shadow-xs" />
                          </div>
                        </div>

                        <div className="flex flex-col text-left leading-none pr-0.5">
                          <span className="font-extrabold text-[8.5px] tracking-wider uppercase text-slate-800 dark:text-slate-200 group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors whitespace-nowrap">
                            Preview Tour
                          </span>
                          <span className="text-[7px] font-mono font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest mt-0.5 whitespace-nowrap">
                            {scenarioStationCount} Stations
                          </span>
                        </div>
                      </button>

                      {/* Rich Visual Tour Preview Popover */}
                      {showSkillsLabTourPreview && (
                        <div className="absolute top-full left-1/2 -translate-x-1/2 sm:left-0 sm:translate-x-0 mt-2 w-[calc(100vw-2rem)] max-w-sm sm:w-96 bg-slate-950/95 backdrop-blur-2xl border-2 border-emerald-400/60 rounded-2xl p-3.5 sm:p-4 shadow-2xl z-50 animate-in fade-in slide-in-from-top-2 duration-200 ring-1 ring-white/10 text-white font-sans text-left">
                          {/* Header */}
                          <div className="flex items-center justify-between pb-2.5 border-b border-white/10 mb-3">
                            <div className="flex items-center gap-2">
                              <div className="w-6 h-6 rounded-lg bg-emerald-500/20 text-emerald-300 flex items-center justify-center border border-emerald-400/40">
                                <Eye className="w-3.5 h-3.5" />
                              </div>
                              <div>
                                <h4 className="text-xs font-black text-white uppercase tracking-wider">
                                  360° VR Tour Preview
                                </h4>
                                <p className="text-[10px] text-slate-400 leading-tight">
                                  Visual sense of room stations before starting
                                </p>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => setShowSkillsLabTourPreview(false)}
                              className="p-1 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition-colors cursor-pointer"
                              title="Close preview"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>

                          {/* High-Fidelity Room Visual Snapshot */}
                          <div className="relative aspect-video rounded-xl overflow-hidden border border-white/20 mb-3 shadow-inner bg-black group/img">
                            <img
                              src={scenarioPreviewImage}
                              alt="Clinical VR environment preview"
                              className="w-full h-full object-cover group-hover/img:scale-105 transition-transform duration-500"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-transparent to-transparent flex flex-col justify-end p-2.5">
                              <span className="text-[10px] font-mono font-bold text-emerald-300 uppercase tracking-widest flex items-center gap-1">
                                <Sparkles className="w-3 h-3 text-emerald-400" /> 360° VR Spherical Environment
                              </span>
                              <span className="text-xs font-black text-white truncate drop-shadow-md">
                                {activeScenario?.title || selectedSkill.name}
                              </span>
                            </div>
                          </div>

                          {/* Station Summary */}
                          <div className="flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-lg bg-white/5 border border-white/10 mb-3">
                            <span className="text-[10px] font-mono font-bold text-slate-300 uppercase tracking-wider flex items-center gap-1.5">
                              <Compass className="w-3 h-3 text-emerald-400" />
                              {scenarioStationCount} Room Stations
                            </span>
                            <span className="text-[10px] font-mono font-bold text-emerald-400 uppercase tracking-wider flex items-center gap-1">
                              <Headphones className="w-3 h-3" /> Audio Guided
                            </span>
                          </div>

                          {/* Station Badges Itinerary */}
                          {activeScenario?.hotspots && activeScenario.hotspots.length > 0 && (
                            <div className="space-y-1.5 mb-3.5">
                              <span className="text-[9px] font-mono font-bold text-slate-400 uppercase tracking-widest block">
                                Stations Visited on Tour:
                              </span>
                              <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto pr-1">
                                {activeScenario.hotspots.slice(0, 6).map((hs, idx) => (
                                  <span
                                    key={hs.id || idx}
                                    className="px-2 py-0.5 rounded-md bg-white/10 text-slate-200 text-[10px] font-bold border border-white/10 flex items-center gap-1"
                                  >
                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0" />
                                    {hs.name || hs.label || `Station ${idx + 1}`}
                                  </span>
                                ))}
                                {activeScenario.hotspots.length > 6 && (
                                  <span className="px-2 py-0.5 rounded-md bg-emerald-500/20 text-emerald-300 text-[10px] font-bold border border-emerald-400/30">
                                    +{activeScenario.hotspots.length - 6} more
                                  </span>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Action CTAs */}
                          <div className="flex items-center gap-2 pt-1 border-t border-white/10">
                            <button
                              type="button"
                              onClick={() => {
                                if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
                                  window.speechSynthesis.cancel();
                                }
                                setIsSpeaking(false);
                                setShowSkillsLabTourPreview(false);
                                setAutoStartTourOnLaunch(true);
                                initiateSimulatorMode(selectedSkill, true);
                              }}
                              className="flex-1 py-2 px-3 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white font-extrabold text-xs uppercase tracking-wider flex items-center justify-center gap-1.5 shadow-lg shadow-emerald-500/30 transition-all hover:scale-[1.02] active:scale-95 cursor-pointer"
                            >
                              <Play className="w-3 h-3 fill-current ml-0.5" />
                              Launch Guided Tour
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
                                  window.speechSynthesis.cancel();
                                }
                                setIsSpeaking(false);
                                setShowSkillsLabTourPreview(false);
                                setAutoStartTourOnLaunch(false);
                                initiateSimulatorMode(selectedSkill, false);
                              }}
                              className="px-3 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-slate-200 font-bold text-xs uppercase tracking-wider transition-colors cursor-pointer"
                            >
                              Free-Look
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Study Mode Sub-controls: Interactive Walkthrough vs Examiner Rubric & Speech Rate */}
              {!practiceMode && !simulatorMode && (
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="flex bg-slate-100 dark:bg-slate-950 rounded-2xl p-1 border border-slate-200/60 dark:border-slate-800 gap-1">
                    <button
                      onClick={() => {
                        if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
                          window.speechSynthesis.cancel();
                        }
                        setIsSpeaking(false);
                        setStudyViewMode('slides');
                      }}
                      className={`px-3 py-1.5 rounded-xl text-[9px] font-black tracking-widest uppercase transition-all flex items-center gap-1.5 ${studyViewMode === 'slides' ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow-xs border border-slate-200 dark:border-slate-800' : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'}`}
                    >
                      <Layers size={12} />
                      Interactive Slides
                    </button>
                    <button
                      onClick={() => {
                        if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
                          window.speechSynthesis.cancel();
                        }
                        setIsSpeaking(false);
                        setStudyViewMode('rubric');
                      }}
                      className={`px-3 py-1.5 rounded-xl text-[9px] font-black tracking-widest uppercase transition-all flex items-center gap-1.5 ${studyViewMode === 'rubric' ? 'bg-emerald-600 text-white shadow-xs' : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'}`}
                    >
                      <FileText size={12} />
                      Examiner Rubric
                    </button>
                  </div>

                  {/* Speech rate control */}
                  <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-950 px-2 py-1 rounded-xl border border-slate-200/60 dark:border-slate-800">
                    <Volume2 size={12} className="text-slate-400" />
                    {[1.0, 1.25, 1.5].map((speed) => (
                      <button
                        key={speed}
                        onClick={() => setSpeechSpeed(speed)}
                        className={`px-1.5 py-0.5 rounded text-[8px] font-black ${speechSpeed === speed ? 'bg-emerald-500 text-white' : 'text-slate-500 hover:text-slate-700 dark:text-slate-400'}`}
                      >
                        {speed}x
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {simulatorMode && activeClinicalScenario ? (
            <div id="skills-lab-simulator-container" className="w-full animate-fade-in font-sans">
              <ClinicalRoomStage
                scenario={activeClinicalScenario}
                initialTourMode={autoStartTourOnLaunch}
                onExit={() => {
                  if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
                    window.speechSynthesis.cancel();
                  }
                  setIsSpeaking(false);
                  setAutoStartTourOnLaunch(false);
                  setSimulatorMode(false);
                }}
              />
            </div>
          ) : !practiceMode ? (
            studyViewMode === 'rubric' ? (
              /* EXAMINER CHECKLIST RUBRIC VIEW */
              <div className="w-full space-y-6 animate-fade-in font-sans">
                {/* Header Banner */}
                <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-[2rem] p-6 md:p-8 shadow-sm">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="px-3 py-1 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 text-[8px] font-black rounded-full border border-emerald-200 dark:border-emerald-800/60 uppercase tracking-wider">
                          Official State Board Rubric
                        </span>
                        <span className="text-[10px] font-bold text-slate-400">
                          Clinical Performance & Safety Standards
                        </span>
                      </div>
                      <h2 className="text-2xl md:text-3xl font-black text-slate-900 dark:text-white tracking-tight">
                        {selectedSkill.name}
                      </h2>
                      <p className="text-xs text-slate-500 dark:text-slate-400 font-semibold mt-1 max-w-2xl leading-relaxed">
                        Standardized 3-Phase Clinical Performance Checklist aligned with Credentia, Prometric, and Headmaster testing rubrics. All marked critical milestones must be performed in correct sequence without omission to pass.
                      </p>
                    </div>

                    <div className="flex items-center gap-3 shrink-0">
                      <button
                        onClick={() => {
                          setPrintScope('current');
                          setIsPrintModalOpen(true);
                        }}
                        className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-xl text-xs font-black uppercase tracking-wider transition-all flex items-center gap-2 border border-slate-200 dark:border-slate-700"
                      >
                        <Printer size={14} />
                        <span>Print Rubric</span>
                      </button>
                      <button
                        onClick={() => initiatePracticeMode()}
                        className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-black uppercase tracking-wider transition-all flex items-center gap-2 shadow-md active:scale-95"
                      >
                        <Zap size={14} />
                        <span>Take Test Now</span>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Supplies Card */}
                {(() => {
                  const supplySlide = selectedSkill.slides.find(s => s.type === 'supplies');
                  if (!supplySlide || !supplySlide.data?.supplies?.length) return null;
                  return (
                    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 shadow-sm">
                      <h3 className="text-xs font-black text-indigo-600 dark:text-indigo-400 uppercase tracking-wider flex items-center gap-2 mb-4">
                        <Layers size={14} /> Equipment & Supplies Checklist
                      </h3>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                        {supplySlide.data.supplies.map((item: any, i: number) => (
                          <div key={i} className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200/70 dark:border-slate-700/60 flex items-center gap-2.5">
                            <div className="w-5 h-5 rounded-md bg-indigo-100 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-400 flex items-center justify-center font-black text-[9px] shrink-0">
                              {i + 1}
                            </div>
                            <span className="text-xs font-bold text-slate-800 dark:text-slate-200 leading-tight">
                              {typeof item === 'string' ? item : item.name}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {/* 3-Phase Breakdown */}
                {(() => {
                  const curated = extractCuratedSteps(selectedSkill);
                  const phases: Array<{ name: 'Preparation' | 'Core Procedure' | 'Safety & Exit'; title: string; subtitle: string; badgeBg: string }> = [
                    {
                      name: 'Preparation',
                      title: 'Phase 1: Pre-Procedure Initiation & Environmental Safety',
                      subtitle: 'Mandatory patient greeting, privacy drape, hand decontamination, and bed lock checks.',
                      badgeBg: 'bg-teal-50 dark:bg-teal-950/40 text-teal-700 dark:text-teal-300 border-teal-200 dark:border-teal-800'
                    },
                    {
                      name: 'Core Procedure',
                      title: 'Phase 2: Core Clinical Procedure & Technique',
                      subtitle: 'Direct resident care interventions executed in strictly compliant sequential order.',
                      badgeBg: 'bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 border-indigo-200 dark:border-indigo-800'
                    },
                    {
                      name: 'Safety & Exit',
                      title: 'Phase 3: Post-Procedure Safety & Terminal Exit Protocol',
                      subtitle: 'Lowest bed position, call light placement within direct grasp, and terminal hand hygiene.',
                      badgeBg: 'bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800'
                    }
                  ];

                  return phases.map((phase) => {
                    const phaseSteps = curated.filter(s => s.phase === phase.name);
                    if (phaseSteps.length === 0) return null;

                    return (
                      <div key={phase.name} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 shadow-sm space-y-4">
                        <div className="border-b border-slate-100 dark:border-slate-800 pb-3">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`px-2.5 py-0.5 rounded-md text-[8px] font-black uppercase tracking-wider border ${phase.badgeBg}`}>
                              {phase.name}
                            </span>
                            <span className="text-[9px] font-bold text-slate-400">
                              {phaseSteps.length} Clinical Milestones
                            </span>
                          </div>
                          <h3 className="text-base font-black text-slate-900 dark:text-white">
                            {phase.title}
                          </h3>
                          <p className="text-[11px] text-slate-400 font-semibold mt-0.5">
                            {phase.subtitle}
                          </p>
                        </div>

                        <div className="space-y-3">
                          {phaseSteps.map((step) => (
                            <div
                              key={step.id}
                              className={`p-4 rounded-2xl border transition-all flex flex-col sm:flex-row sm:items-center justify-between gap-3 ${
                                step.isCritical
                                  ? 'bg-amber-50/50 dark:bg-amber-950/20 border-amber-300 dark:border-amber-800/80 shadow-xs'
                                  : 'bg-slate-50/80 dark:bg-slate-800/50 border-slate-200/80 dark:border-slate-700/60'
                              }`}
                            >
                              <div className="flex items-start gap-3.5 min-w-0">
                                <div className={`w-7 h-7 rounded-xl font-black text-xs flex items-center justify-center shrink-0 shadow-xs ${
                                  step.isCritical
                                    ? 'bg-amber-500 text-white'
                                    : 'bg-slate-200 dark:bg-slate-700 text-slate-800 dark:text-slate-200'
                                }`}>
                                  {step.originalIndex + 1}
                                </div>
                                <div className="space-y-1">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-xs font-black text-slate-900 dark:text-white leading-relaxed">
                                      {step.text}
                                    </span>
                                    {step.isCritical && (
                                      <span className="px-2 py-0.5 rounded bg-rose-500 text-white text-[7px] font-black uppercase tracking-wider flex items-center gap-1">
                                        <AlertTriangle size={8} /> Critical Milestone
                                      </span>
                                    )}
                                  </div>
                                  {step.criticalPoints && (
                                    <p className="text-[10px] text-amber-700 dark:text-amber-400 font-bold bg-amber-100/60 dark:bg-amber-950/60 p-2 rounded-lg border border-amber-200 dark:border-amber-900/60 leading-relaxed">
                                      ⚠️ Examiner Note: {step.criticalPoints}
                                    </p>
                                  )}
                                </div>
                              </div>

                              <div className="shrink-0 flex items-center gap-2 self-end sm:self-center">
                                <button
                                  type="button"
                                  onClick={() => handleSpeakText(step.text)}
                                  className="p-2 rounded-lg bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 transition-all"
                                  title="Listen to step pronunciation"
                                >
                                  <Volume2 size={13} />
                                </button>
                                <span className="px-2 py-1 rounded-lg bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 text-[8px] font-black uppercase tracking-wider border border-emerald-200 dark:border-emerald-800/40">
                                  Step #{step.originalIndex + 1}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  });
                })()}

                {/* State Board Automatic Disqualification Traps Callout */}
                {(() => {
                  const criticalSlide = selectedSkill.slides.find(s => s.type === 'criticals');
                  return (
                    <div className="bg-rose-50 dark:bg-rose-950/20 border-2 border-rose-200 dark:border-rose-900/60 rounded-3xl p-6 shadow-sm space-y-3">
                      <div className="flex items-center gap-2 text-rose-700 dark:text-rose-400">
                        <ShieldAlert size={18} />
                        <h3 className="text-sm font-black uppercase tracking-wider">
                          State Board Automatic Disqualification Traps
                        </h3>
                      </div>
                      <p className="text-xs text-rose-900 dark:text-rose-200 font-bold leading-relaxed">
                        Examiners will immediately issue an unrecoverable zero score if any of the following mandatory standards are violated during testing:
                      </p>
                      <ul className="grid grid-cols-1 md:grid-cols-2 gap-2.5 pt-1">
                        {criticalSlide?.data?.criticalPoints ? (
                          <li className="p-3 bg-white/80 dark:bg-slate-900/80 border border-rose-200 dark:border-rose-900/50 rounded-xl text-xs font-bold text-rose-800 dark:text-rose-300">
                            {criticalSlide.data.criticalPoints}
                          </li>
                        ) : null}
                        <li className="p-3 bg-white/80 dark:bg-slate-900/80 border border-rose-200 dark:border-rose-900/50 rounded-xl text-xs font-bold text-rose-800 dark:text-rose-300">
                          Failure to verify and declare water temperature prior to direct resident contact.
                        </li>
                        <li className="p-3 bg-white/80 dark:bg-slate-900/80 border border-rose-200 dark:border-rose-900/50 rounded-xl text-xs font-bold text-rose-800 dark:text-rose-300">
                          Leaving bed elevated or bed side-rails down when turning back on resident.
                        </li>
                        <li className="p-3 bg-white/80 dark:bg-slate-900/80 border border-rose-200 dark:border-rose-900/50 rounded-xl text-xs font-bold text-rose-800 dark:text-rose-300">
                          Placing dirty resident linens onto clean overbed table or floor.
                        </li>
                        <li className="p-3 bg-white/80 dark:bg-slate-900/80 border border-rose-200 dark:border-rose-900/50 rounded-xl text-xs font-bold text-rose-800 dark:text-rose-300">
                          Exiting patient room without placing call light within direct resident grasp.
                        </li>
                      </ul>
                    </div>
                  );
                })()}
              </div>
            ) : (
            /* DUAL PANE LAYOUT: Navigation list on Left, Active workspace on Right */
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start w-full">
              
              {/* LEFT COLUMN: ACTIVE STEP LIST (TOC) FOR HIGHEST USABILITY AND NAVIGATION */}
              <div className="lg:col-span-4 bg-white dark:bg-slate-955 border border-slate-200 dark:border-slate-850 rounded-[2rem] p-5 shadow-xs space-y-4 lg:sticky lg:top-24 hidden md:block">
                <div className="flex flex-col gap-1 border-b border-slate-100 dark:border-slate-850 pb-3">
                   <span className="text-[8px] font-black text-rose-500 uppercase tracking-widest">Steps</span>
                   <h4 className="text-[11px] font-extrabold text-slate-950 dark:text-slate-100 uppercase tracking-wider flex items-center gap-1.5">
                     <ListChecks size={14} className="text-emerald-500" strokeWidth={3.5} />
                     Lesson Steps
                   </h4>
                </div>

                <div className="space-y-2 pr-1 max-h-[calc(100vh-280px)] overflow-y-auto custom-scrollbar">
                  {selectedSkill.slides.map((slide, idx) => {
                    const isActive = idx === currentSlideIndex;
                    let slideTypeLabel = "Step";
                    let badgeStyle = "bg-slate-100 text-slate-705 dark:bg-slate-900 dark:text-slate-400";
                    const slideHasVideo = slide.type === 'embedded' || Boolean(slide.data?.embedUrl) || Boolean(slide.data?.embedCode) || Boolean(slide.data?.videoUrl) || Boolean(slide.data?.youtubeUrl) || Boolean((slide as any).videoUrl);
                    
                    if (slide.type === 'overview') {
                      slideTypeLabel = "Overview";
                      badgeStyle = "bg-teal-50 text-teal-700 dark:bg-teal-950/20 dark:text-teal-400 border border-teal-100 dark:border-teal-900/50";
                    } else if (slide.type === 'supplies') {
                      slideTypeLabel = "Supplies";
                      badgeStyle = "bg-indigo-50 text-indigo-700 dark:bg-indigo-950/20 dark:text-indigo-400 border border-indigo-100 dark:border-indigo-900/50";
                    } else if (slide.type === 'criticals') {
                      slideTypeLabel = "⚠️ Failure";
                      badgeStyle = "bg-rose-50 text-rose-700 dark:bg-rose-950/20 dark:text-rose-400 border border-rose-100 dark:border-rose-900/50";
                    } else if (slide.type === 'reporting') {
                      slideTypeLabel = "Saying steps";
                      badgeStyle = "bg-amber-50 text-amber-700 dark:bg-amber-950/20 dark:text-amber-450 border border-amber-100 dark:border-amber-900/50";
                    } else if (slide.type === 'completion') {
                      slideTypeLabel = "Safety steps";
                      badgeStyle = "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/20 dark:text-emerald-400 border border-emerald-100 dark:border-emerald-900/50";
                    } else if (slide.type === 'embedded') {
                      slideTypeLabel = "Video Demo";
                      badgeStyle = "bg-indigo-50 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-900/60";
                    }

                    return (
                      <button
                        key={slide.id}
                        onClick={() => {
                          setCurrentSlideIndex(idx);
                          setShowHint(false);
                        }}
                        className={`w-full p-3.5 rounded-2xl border-2 text-left font-bold text-xs flex justify-between items-center transition-all ${
                          isActive 
                            ? 'border-emerald-500 bg-emerald-50/20 dark:bg-emerald-950/20' 
                            : 'border-slate-100 bg-slate-50/50 hover:bg-slate-100 dark:border-slate-850 dark:bg-slate-900/10'
                        }`}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className={`w-7 h-7 rounded-lg font-black text-[10px] flex items-center justify-center shrink-0 ${
                            isActive 
                              ? 'bg-emerald-555 text-white' 
                              : 'bg-white border border-slate-205 text-slate-400 dark:bg-slate-900 dark:border-slate-800'
                          }`}>
                            {idx + 1}
                          </div>
                          <div className="text-left min-w-0">
                            <p className={`font-black text-[11px] leading-tight truncate max-w-[130px] flex items-center gap-1.5 ${
                              isActive ? 'text-emerald-805 dark:text-emerald-400' : 'text-slate-955 dark:text-slate-100'
                            }`}>
                              {slideHasVideo && <PlayCircle size={11} className="text-indigo-600 dark:text-indigo-400 shrink-0" />}
                              <span className="truncate">{slide.title || slideTypeLabel}</span>
                            </p>
                            <p className="text-[7.5px] text-slate-400 font-extrabold tracking-wider mt-0.5 uppercase flex items-center gap-1">
                              <span>Step {idx + 1}</span>
                              {slideHasVideo && slide.type !== 'embedded' && (
                                <span className="text-indigo-600 dark:text-indigo-400 font-black flex items-center gap-0.5">
                                  • <Video size={7} /> Video
                                </span>
                              )}
                            </p>
                          </div>
                        </div>

                        <span className={`px-1.5 py-0.5 text-[6.5px] font-black uppercase tracking-wider rounded flex items-center gap-1 shrink-0 ${
                          isActive ? 'bg-emerald-500 text-white shadow-xs' : badgeStyle
                        }`}>
                          {slideHasVideo && slide.type !== 'embedded' && <Video size={7} className="shrink-0" />}
                          {slideTypeLabel}
                        </span>
                      </button>
                    );
                  })}
                  
                  {/* Quiz shortcut in Left TOC navigation list */}
                  {selectedSkill.quiz && selectedSkill.quiz.length > 0 && (
                    <div className="pt-2 border-t border-dashed border-slate-205 dark:border-slate-800">
                      <button 
                        onClick={() => {
                          setShowQuiz(true);
                          setQuizIndex(0);
                          setQuizScore(0);
                          setIsQuizCompleted(false);
                          setSelectedOption(null);
                          setIsAnswered(false);
                          setShowHint(false);
                        }}
                        className="w-full p-4 rounded-2xl bg-indigo-50 border-2 border-indigo-100 hover:border-indigo-300 dark:bg-indigo-950/20 dark:border-indigo-900 hover:bg-indigo-100 font-black text-xs text-indigo-700 dark:text-indigo-400 flex items-center justify-between shadow-inner group transition-all"
                      >
                        <div className="flex items-center gap-2.5">
                          <Brain size={14} className="text-indigo-500 animate-pulse" />
                          <div className="text-left">
                            <span className="block leading-none text-[10px]">PRACTICE QUIZ</span>
                            <span className="text-[7px] font-bold text-slate-400 tracking-wider mt-0.5 uppercase block">Test your knowledge</span>
                          </div>
                        </div>
                        <ArrowRight size={14} strokeWidth={2.5} className="group-hover:translate-x-1 transition-transform" />
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* RIGHT COLUMN: ACTIVE SLIDE VIEW CARD */}
              <div className="lg:col-span-8 flex flex-col space-y-6">
                 <div key={`${selectedSkill.id}-${currentSlideIndex}`} className="w-full animate-slide-up">
                    {selectedSkill.slides.length > 0 ? (
                       renderSkillSlide(selectedSkill, selectedSkill.slides[currentSlideIndex], currentSlideIndex, showHint, setExpandedImage, false, handleSpeakText, isSpeaking)
                    ) : (
                      <div className="bg-white p-20 rounded-[3rem] border-4 border-dashed border-slate-200 flex flex-col items-center justify-center text-center gap-6 opacity-60">
                         <Layers size={64} className="text-slate-300" />
                          <p className="font-black text-slate-400 tracking-widest">This skill guide is temporarily empty.<br/>Please pick another skill above.</p>
                          <button onClick={handleClose} className="px-10 py-4 bg-slate-900 text-white rounded-2xl font-black text-[10px] tracking-widest">Back to All Skills</button>
                      </div>
                    )}
                 </div>
                 
                 {/* Mobile navigation dots */}
                 <div className="flex justify-center gap-1.5 md:hidden">
                   {selectedSkill.slides.map((_, i) => (
                     <button 
                       key={i} 
                       onClick={() => setCurrentSlideIndex(i)} 
                       className={`h-2 text-[9px] rounded-full transition-all ${i === currentSlideIndex ? 'w-8 bg-emerald-500' : 'w-2 bg-slate-200 dark:bg-slate-800'}`} 
                     />
                   ))}
                 </div>

                 <AdUnit placement="skills_lab_bottom" className="mt-12" />
              </div>

            </div>
            )
          ) : (
            /* Page 3: Test Mode - Visual Theme: Modern Focused Exam-Simulator */
            <div className="w-full space-y-6 animate-[slide-up_0.3s_ease] pb-16 font-sans" id="skills-practice-viewport">
              
              {/* SIMULATED PATIENT CLINICAL HUD */}
              {(() => {
                const patientInfo = getSimulatedPatientInfo(selectedSkill, scenarioVariation);
                return (
                  <div className="bg-gradient-to-r from-slate-900 via-slate-850 to-slate-900 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 text-white rounded-[2rem] p-6 shadow-xl border border-slate-700/80 relative overflow-hidden">
                    <div className="absolute -right-10 -bottom-10 w-48 h-48 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />
                    
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 relative z-10">
                      {/* Resident Demographic Banner */}
                      <div className="flex items-center gap-4">
                        <div className="w-14 h-14 rounded-2xl bg-emerald-500/20 border border-emerald-500/40 text-emerald-400 flex items-center justify-center shrink-0 shadow-inner">
                          <User size={28} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <span className="px-2.5 py-0.5 rounded-full text-[8.5px] font-black uppercase tracking-wider bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                              Active Resident Scenario
                            </span>
                            <span className="text-[10px] font-black text-slate-400">
                              {patientInfo.roomNumber}
                            </span>
                          </div>
                          <h2 className="text-xl md:text-2xl font-black text-white tracking-tight">
                            {patientInfo.residentName}
                          </h2>
                          <div className="flex items-center gap-3 text-[11px] text-slate-300 font-semibold mt-0.5 flex-wrap">
                            <span className="flex items-center gap-1 text-slate-300">
                              <HeartPulse size={13} className="text-rose-400" />
                              {patientInfo.condition}
                            </span>
                            <span className="text-slate-500">•</span>
                            <span className="text-amber-300 font-bold">
                              ⚠️ {patientInfo.precaution}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Live HUD Controls: Countdown Timer + State Board Selector */}
                      <div className="flex flex-wrap items-center gap-3">
                        {/* Live Timer Pill */}
                        <div className="flex items-center gap-3 bg-slate-800/90 border border-slate-700/80 px-4 py-2 rounded-2xl shadow-inner">
                          <Clock size={16} className={`shrink-0 ${practiceTimerSeconds < 60 ? 'text-rose-400 animate-pulse' : 'text-emerald-400'}`} />
                          <div>
                            <span className="text-[8px] font-black uppercase tracking-wider text-slate-400 block">Exam Clock</span>
                            <span className={`text-base font-black font-mono tracking-tight leading-none ${practiceTimerSeconds < 60 ? 'text-rose-400 animate-pulse' : 'text-white'}`}>
                              {Math.floor(practiceTimerSeconds / 60)}:{(practiceTimerSeconds % 60).toString().padStart(2, '0')}
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => setIsTimerRunning(!isTimerRunning)}
                            className={`px-2 py-0.5 rounded-lg text-[8px] font-black uppercase tracking-wider transition-all ${isTimerRunning ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30' : 'bg-emerald-500 text-white shadow'}`}
                          >
                            {isTimerRunning ? "Pause" : "Start"}
                          </button>
                          <button
                            type="button"
                            onClick={() => setPracticeTimerSeconds(prev => prev === 300 ? 600 : 300)}
                            className="text-[8px] text-slate-400 hover:text-white font-bold transition-colors"
                            title="Toggle 5m / 10m"
                          >
                            {practiceTimerSeconds === 300 ? "10m" : "5m"}
                          </button>
                        </div>

                        {/* Board Standard Pill */}
                        <div className="flex bg-slate-800/90 border border-slate-700/80 p-1 rounded-2xl">
                          {(['Credentia', 'Prometric', 'Headmaster'] as const).map((vendor) => (
                            <button
                              key={vendor}
                              type="button"
                              onClick={() => {
                                setPracticeVendor(vendor);
                                setPracticeResult(null);
                              }}
                              className={`px-3 py-1.5 rounded-xl text-[9px] font-black tracking-wider uppercase transition-all ${
                                practiceVendor === vendor 
                                  ? 'bg-emerald-600 text-white shadow-sm' 
                                  : 'text-slate-400 hover:text-white'
                              }`}
                            >
                              {vendor}
                            </button>
                          ))}
                        </div>

                        {/* Settings toggle */}
                        <button
                          onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}
                          className={`p-2.5 rounded-2xl border transition-all ${
                            showAdvancedSettings 
                              ? 'bg-emerald-600 text-white border-emerald-500' 
                              : 'bg-slate-800/90 hover:bg-slate-700 text-slate-300 border-slate-700/80'
                          }`}
                          title="Simulator Settings"
                        >
                          <SlidersHorizontal size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* COLLAPSED ADVANCED SETTINGS EXPANDER */}
              {showAdvancedSettings && (
                <div className="p-5 bg-slate-50 dark:bg-slate-900/90 border border-slate-200 dark:border-slate-800 rounded-2xl space-y-4 animate-fade-in">
                  <div className="flex justify-between items-center border-b border-slate-200 dark:border-slate-800 pb-2">
                    <h3 className="text-xs font-black uppercase tracking-wider text-slate-800 dark:text-slate-200">Simulator Settings</h3>
                    <button onClick={() => setShowAdvancedSettings(false)} className="text-xs font-bold text-slate-400 hover:text-slate-600">Close ✕</button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    {/* Exam Company */}
                    <div className="space-y-1">
                      <label className="text-[9px] font-black uppercase text-slate-400">Exam Company</label>
                      <div className="flex bg-white dark:bg-slate-950 p-1 rounded-xl border border-slate-200 dark:border-slate-800 gap-1">
                        {(['Credentia', 'Prometric', 'Headmaster'] as const).map((vendor) => (
                          <button
                            key={vendor}
                            onClick={() => {
                              setPracticeVendor(vendor);
                              setPracticeResult(null);
                            }}
                            className={`flex-1 py-1 text-[9px] font-black uppercase rounded-lg transition-all ${practiceVendor === vendor ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900' : 'text-slate-500'}`}
                          >
                            {vendor}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Scenario Strain */}
                    <div className="space-y-1">
                      <label className="text-[9px] font-black uppercase text-slate-400">Patient Scenario</label>
                      <select
                        value={scenarioVariation}
                        onChange={(e) => setScenarioVariation(e.target.value as any)}
                        className="w-full text-xs font-bold bg-white dark:bg-slate-950 text-slate-800 dark:text-slate-200 border border-slate-200 dark:border-slate-800 rounded-xl p-2 outline-none"
                      >
                        <option value="standard">Standard Responsive Care</option>
                        <option value="unresponsive">Unresponsive / Comatose Patient</option>
                        <option value="combative">Agitated / Restless Patient</option>
                        <option value="left_weakness">Left-Side Weakness (Stroke)</option>
                      </select>
                    </div>

                    {/* Immediate Fail Mode */}
                    <div className="space-y-1">
                      <label className="text-[9px] font-black uppercase text-slate-400">Immediate-Fail Mode</label>
                      <div className="flex items-center justify-between p-2 bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl">
                        <span className="text-xs font-bold text-slate-700 dark:text-slate-300">
                          {immediateFailMode ? "🚫 Active (Hardcore)" : "🛡️ Inactive (Safe)"}
                        </span>
                        <button
                          type="button"
                          onClick={() => {
                            setImmediateFailMode(!immediateFailMode);
                            setPracticeResult(null);
                          }}
                          className={`w-8 h-4.5 rounded-full p-0.5 transition-colors shrink-0 relative ${immediateFailMode ? 'bg-rose-500' : 'bg-slate-300 dark:bg-slate-700'}`}
                        >
                          <div className={`w-3.5 h-3.5 rounded-full bg-white shadow transform transition-transform ${immediateFailMode ? 'translate-x-[14px]' : 'translate-x-0'}`} />
                        </button>
                      </div>
                    </div>

                    {/* Sort Pool Order */}
                    <div className="space-y-1">
                      <label className="text-[9px] font-black uppercase text-slate-400">Step Pool Order</label>
                      <div className="flex bg-white dark:bg-slate-950 p-1 rounded-xl border border-slate-200 dark:border-slate-800 gap-1">
                        {(['shuffled', 'alphabetical', 'original'] as const).map((order) => (
                          <button
                            key={order}
                            type="button"
                            onClick={() => setPoolSortOrder(order)}
                            className={`flex-1 py-1 text-[9px] font-black uppercase rounded-lg transition-all ${poolSortOrder === order ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900' : 'text-slate-500'}`}
                          >
                            {order === 'shuffled' ? 'Shuffled' : order === 'alphabetical' ? 'A-Z' : 'Original'}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* COLLAPSED SAFETY RULES BANNER */}
              <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900/40 rounded-2xl p-3.5 flex items-center justify-between transition-all">
                <div className="flex items-center gap-2 text-amber-800 dark:text-amber-300 text-xs font-bold">
                  <ShieldAlert size={16} className="text-amber-500 shrink-0" />
                  <span>🛡️ {practiceVendor} Safety Rules — Critical guidelines to review before you test</span>
                </div>
                <button
                  onClick={() => setShowSafetyRulesBanner(!showSafetyRulesBanner)}
                  className="px-3 py-1 bg-amber-500/10 hover:bg-amber-500/20 text-amber-700 dark:text-amber-300 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all"
                >
                  {showSafetyRulesBanner ? 'Hide Rules' : 'Show Rules →'}
                </button>
              </div>

              {/* EXPANDABLE SAFETY RULES DRAWER */}
              {showSafetyRulesBanner && (
                <div className="p-5 bg-amber-50/50 dark:bg-amber-950/10 border border-amber-200 dark:border-amber-900/30 rounded-2xl space-y-3 animate-fade-in">
                  <h4 className="text-xs font-black uppercase tracking-wider text-amber-900 dark:text-amber-200">
                    Critical Safety Guidelines for {practiceVendor}
                  </h4>
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs font-medium text-amber-950 dark:text-amber-300">
                    <li className="flex items-start gap-2">
                      <span className="text-rose-500 font-bold">1.</span> Hand Hygiene must be performed before and after resident contact.
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-rose-500 font-bold">2.</span> Resident privacy (curtain/door) must be provided prior to care.
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-rose-500 font-bold">3.</span> Bed brakes must be locked and bed lowered to lowest position when leaving.
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-rose-500 font-bold">4.</span> Call light must be placed within resident's direct reach before exiting.
                    </li>
                  </ul>
                </div>
              )}

              {/* TWO-COLUMN TASK AREA */}
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
                
                {/* LEFT COLUMN: STEPS TO CHOOSE FROM (7 COLS ON LARGE) */}
                <div className="lg:col-span-7 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 shadow-sm space-y-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 dark:border-slate-800 pb-4">
                    <div>
                      <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Available Steps</span>
                      <h3 className="text-sm font-black text-slate-900 dark:text-white uppercase tracking-wider flex items-center gap-1.5">
                        <Layers size={14} className="text-emerald-500" /> Steps to Choose From
                      </h3>
                    </div>
                    <span className="text-[9px] font-black bg-slate-100 dark:bg-slate-800 px-2.5 py-1 text-slate-600 dark:text-slate-300 rounded-lg w-fit">
                      {practiceAvailableSteps.length} Steps Remaining
                    </span>
                  </div>

                  {/* Phase Filter Tabs + Search Filter */}
                  <div className="space-y-3">
                    {/* Phase Filter Tabs */}
                    <div className="flex flex-wrap gap-1.5">
                      {(['all', 'Preparation', 'Core Procedure', 'Safety & Exit'] as const).map((phase) => {
                        const count = phase === 'all' 
                          ? practiceAvailableSteps.length 
                          : practiceAvailableSteps.filter(s => s.phase === phase).length;
                        const isSelected = practiceStepFilter === phase;
                        
                        return (
                          <button
                            key={phase}
                            type="button"
                            onClick={() => setPracticeStepFilter(phase)}
                            className={`px-3 py-1.5 rounded-xl text-[9px] font-black uppercase tracking-wider transition-all flex items-center gap-1.5 ${
                              isSelected
                                ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900 shadow-sm'
                                : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
                            }`}
                          >
                            <span>{phase === 'all' ? 'All Steps' : phase}</span>
                            <span className={`px-1.5 py-0.2 rounded-full text-[8px] ${isSelected ? 'bg-white/20 text-white dark:bg-slate-900/20 dark:text-slate-900' : 'bg-slate-200 dark:bg-slate-700'}`}>
                              {count}
                            </span>
                          </button>
                        );
                      })}
                    </div>

                    {/* Step Search Filter */}
                    <div className="relative">
                      <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input
                        type="text"
                        value={practiceStepSearch}
                        onChange={(e) => setPracticeStepSearch(e.target.value)}
                        placeholder="Search clinical actions (e.g., gloves, sanitize, curtain)..."
                        className="w-full pl-9 pr-8 py-2 bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700/80 rounded-xl text-xs font-semibold text-slate-900 dark:text-white placeholder:text-slate-400 outline-none focus:border-emerald-500 transition-colors"
                      />
                      {practiceStepSearch && (
                        <button
                          type="button"
                          onClick={() => setPracticeStepSearch('')}
                          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                        >
                          <X size={13} />
                        </button>
                      )}
                    </div>
                  </div>

                  {practiceAvailableSteps.length === 0 ? (
                    <div className="bg-slate-50 dark:bg-slate-800/40 rounded-2xl py-12 px-6 text-center border border-dashed border-slate-200 dark:border-slate-700 space-y-2">
                      <CheckCircle2 size={32} className="text-emerald-500 mx-auto animate-bounce" />
                      <p className="text-xs font-black text-slate-900 dark:text-white uppercase tracking-wider">All steps placed!</p>
                      <p className="text-[10px] text-slate-400 font-medium">Review your sequence on the right, then click Grade My Steps.</p>
                    </div>
                  ) : (
                    <div className="space-y-2.5 max-h-[580px] overflow-y-auto pr-1">
                      {(() => {
                        let steps = [...practiceAvailableSteps];
                        if (practiceStepFilter !== 'all') {
                          steps = steps.filter(s => s.phase === practiceStepFilter);
                        }
                        if (practiceStepSearch.trim()) {
                          const query = practiceStepSearch.toLowerCase();
                          steps = steps.filter(s => s.text.toLowerCase().includes(query));
                        }
                        if (poolSortOrder === 'alphabetical') {
                          steps.sort((a, b) => a.text.localeCompare(b.text));
                        } else if (poolSortOrder === 'original') {
                          steps.sort((a, b) => a.originalIndex - b.originalIndex);
                        }

                        if (steps.length === 0) {
                          return (
                            <div className="p-8 text-center bg-slate-50 dark:bg-slate-800/30 rounded-2xl border border-dashed border-slate-200 dark:border-slate-700">
                              <p className="text-xs font-bold text-slate-500">No steps match your filter</p>
                              <button
                                type="button"
                                onClick={() => { setPracticeStepFilter('all'); setPracticeStepSearch(''); }}
                                className="mt-2 text-[9px] font-black text-emerald-600 uppercase tracking-wider hover:underline"
                              >
                                Reset Search Filters
                              </button>
                            </div>
                          );
                        }

                        return steps.map((step) => {
                          const phaseBadgeColor = 
                            step.phase === 'Preparation'
                              ? 'bg-teal-50 dark:bg-teal-950/40 text-teal-700 dark:text-teal-300 border-teal-200 dark:border-teal-800/60'
                              : step.phase === 'Safety & Exit'
                              ? 'bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800/60'
                              : 'bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 border-indigo-200 dark:border-indigo-800/60';

                          return (
                            <div
                              key={step.id}
                              onClick={() => addStepToSelection(step)}
                              className="p-4 rounded-2xl text-left text-xs font-bold cursor-pointer transition-all active:scale-[0.98] bg-slate-50 hover:bg-emerald-50/20 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700/80 text-slate-800 dark:text-slate-200 hover:border-emerald-400 shadow-xs group"
                            >
                              <div className="flex items-center justify-between gap-2 mb-2">
                                <span className={`px-2 py-0.5 rounded text-[7.5px] font-black uppercase tracking-wider border ${phaseBadgeColor}`}>
                                  {step.phase || 'Procedure'}
                                </span>
                                {step.isCritical && (
                                  <span className="px-2 py-0.5 rounded bg-rose-500 text-white text-[7px] font-black uppercase tracking-wider flex items-center gap-1">
                                    <AlertTriangle size={8} /> Critical Milestone
                                  </span>
                                )}
                              </div>
                              <p className="leading-relaxed font-bold">{step.text}</p>
                              <div className="flex justify-between items-center mt-3 pt-2 border-t border-slate-100 dark:border-slate-700/50">
                                <span className="text-[8px] font-black text-slate-400 uppercase tracking-wider">
                                  Tap card to add
                                </span>
                                <span className="text-[8.5px] text-emerald-600 dark:text-emerald-400 font-black uppercase tracking-wider flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                  + Place in Sequence →
                                </span>
                              </div>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  )}
                </div>

                {/* RIGHT COLUMN: YOUR CLINICAL SEQUENCE (5 COLS ON LARGE) */}
                <div className="lg:col-span-5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 shadow-sm space-y-4 lg:sticky lg:top-24">
                  <div className="flex justify-between items-center border-b border-slate-100 dark:border-slate-800 pb-3">
                    <div>
                      <span className="text-[9px] font-black text-emerald-500 uppercase tracking-widest">Candidate Sequence</span>
                      <h3 className="text-sm font-black text-slate-900 dark:text-white uppercase tracking-wider flex items-center gap-1.5">
                        <ClipboardCheck size={14} className="text-emerald-500" /> Your Procedure Order
                      </h3>
                    </div>
                    <span className="text-[9px] font-black bg-emerald-50 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-400 px-2.5 py-1 rounded-lg">
                      {practiceSelectedSteps.length} Placed
                    </span>
                  </div>

                  {practiceSelectedSteps.length === 0 ? (
                    <div className="bg-slate-50 dark:bg-slate-800/40 rounded-2xl py-16 text-center border border-dashed border-slate-200 dark:border-slate-700 space-y-3">
                      <Zap size={32} className="text-slate-400 mx-auto animate-pulse" />
                      <div>
                        <p className="text-xs font-black text-slate-800 dark:text-white uppercase tracking-wider">Your sequence is empty</p>
                        <p className="text-[10px] text-slate-400 max-w-xs mx-auto mt-1 font-semibold leading-relaxed">
                          Tap steps from the left pool to construct your clinical procedure in exact chronological order.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="space-y-2.5 max-h-[500px] overflow-y-auto pr-1">
                        {practiceSelectedSteps.map((step, idx) => (
                          <div
                            key={step.id}
                            className="p-3.5 bg-slate-50 dark:bg-slate-800/80 border border-emerald-500/30 rounded-2xl flex gap-3 items-center justify-between"
                          >
                            <div className="flex gap-3 items-center min-w-0">
                              <div className="w-7 h-7 rounded-xl bg-emerald-500 text-white font-black text-xs flex items-center justify-center shrink-0">
                                {idx + 1}
                              </div>
                              <div className="min-w-0">
                                {step.phase && (
                                  <span className="text-[7.5px] font-black uppercase text-slate-400 tracking-wider block mb-0.5">
                                    {step.phase}
                                  </span>
                                )}
                                <p className="text-xs font-bold text-slate-900 dark:text-white leading-snug text-left min-w-0">
                                  {step.text}
                                </p>
                              </div>
                            </div>

                            <div className="flex items-center gap-1 shrink-0">
                              <button
                                onClick={() => moveStepUp(idx)}
                                disabled={idx === 0}
                                className="p-1.5 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg text-slate-500 disabled:opacity-20 transition-colors"
                                title="Move Up"
                              >
                                <ArrowUp size={14} />
                              </button>
                              <button
                                onClick={() => moveStepDown(idx)}
                                disabled={idx === practiceSelectedSteps.length - 1}
                                className="p-1.5 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg text-slate-500 disabled:opacity-20 transition-colors"
                                title="Move Down"
                              >
                                <ArrowDown size={14} />
                              </button>
                              <button
                                onClick={() => removeStepFromSelection(step)}
                                className="p-1.5 bg-rose-50 hover:bg-rose-100 text-rose-600 dark:bg-rose-950/40 rounded-lg transition-colors"
                                title="Remove Step"
                              >
                                <X size={14} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Action submission bar */}
                      <div className="flex flex-col sm:flex-row justify-between items-center pt-4 border-t border-slate-100 dark:border-slate-800 gap-3">
                        <button
                          onClick={() => initiatePracticeMode()}
                          className="w-full sm:w-auto px-4 py-2.5 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 transition-all"
                        >
                          <RotateCcw size={13} />
                          Clear All
                        </button>
                        
                        <button
                          onClick={evaluatePracticeOrder}
                          disabled={isEvaluatingPractice || practiceSelectedSteps.length === 0}
                          className="w-full sm:w-auto px-6 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white font-black text-xs uppercase tracking-wider rounded-xl disabled:opacity-30 transition-all flex items-center justify-center gap-1.5 shadow-md active:scale-95"
                        >
                          {isEvaluatingPractice ? (
                            <>
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              Grading...
                            </>
                          ) : (
                            <>
                              <Award size={14} />
                              Grade My Steps
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </div>

              </div>

              {/* EVALUATION RESULT HIGHLIGHT MODULE */}
              {practiceResult && (
                <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-[2.5rem] p-8 shadow-xl space-y-8 animate-[pop_0.4s_ease-out]">
                  
                  {/* KPI and Status banner */}
                  <div className="flex flex-col md:flex-row gap-8 items-center border-b border-dashed border-slate-100 dark:border-slate-800 pb-8">
                    
                    {/* Circular metric dashboard */}
                    <div className="relative w-32 h-32 flex items-center justify-center shrink-0">
                      <svg className="w-full h-full transform -rotate-90">
                        <circle
                          cx="64"
                          cy="64"
                          r="52"
                          className="stroke-slate-100 dark:stroke-slate-800"
                          strokeWidth="10"
                          fill="transparent"
                        />
                        <circle
                          cx="64"
                          cy="64"
                          r="52"
                          className={practiceResult.passed ? "stroke-emerald-500" : "stroke-rose-500"}
                          strokeWidth="10"
                          fill="transparent"
                          strokeDasharray={2 * Math.PI * 52}
                          strokeDashoffset={2 * Math.PI * 52 * (1 - practiceResult.score / 100)}
                          strokeLinecap="round"
                        />
                      </svg>
                      <div className="absolute text-center space-y-0.5">
                        <span className="text-4xl font-black tracking-tight text-slate-950 dark:text-white">
                          {practiceResult.score}%
                        </span>
                        <span className="block text-[7.5px] font-black text-slate-400 tracking-widest uppercase leading-none">
                          Accuracy
                        </span>
                      </div>
                    </div>

                    <div className="space-y-4 text-center md:text-left flex-1">
                      <div>
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[8px] font-black uppercase tracking-wider border ${
                          practiceResult.passed 
                            ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' 
                            : 'bg-rose-500/10 text-rose-500 border-rose-500/20'
                        }`}>
                          {practiceResult.passed ? <CheckCircle2 size={10} /> : <AlertCircle size={10} />}
                          {practiceResult.passed ? "Passed State Standards! ✅" : "Failed Clinical Standards ❌"}
                        </span>
                        <h3 className="text-xl font-black text-slate-950 dark:text-white mt-1.5 tracking-tight">
                          {practiceVendor} Exam Room Evaluation
                        </h3>
                      </div>

                      <div className="space-y-1">
                        {practiceResult.feedback.map((line, i) => (
                          <p key={i} className="text-xs text-slate-600 dark:text-slate-400 font-extrabold leading-relaxed">
                            • {line}
                          </p>
                        ))}
                      </div>

                      {practiceResult.missedCritical && (
                        <div className="p-4 bg-rose-500/10 border border-rose-500/20 rounded-2xl text-left text-xs text-rose-700 dark:text-rose-400 font-bold space-y-1">
                          <p className="flex items-center gap-1.5 text-[9px] font-black text-rose-600 uppercase tracking-widest leading-none">
                            <AlertTriangle size={12} />
                            Critical Safety Step Infraction
                          </p>
                          <p className="leading-relaxed">
                            "{practiceResult.criticalPointText}"
                          </p>
                          <p className="text-[8.5px] opacity-75 font-semibold leading-normal">
                            State evaluators will fail a candidate on the actual exam if this critical step is omitted or performed out of sequence.
                          </p>
                        </div>
                      )}

                      {/* Action Buttons: Retry Misplaced vs Restart vs Rubric */}
                      <div className="flex flex-wrap items-center gap-3 pt-2">
                        {!practiceResult.passed && (
                          <button
                            type="button"
                            onClick={retryMisplacedSteps}
                            className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-black uppercase tracking-wider transition-all flex items-center gap-2 shadow-sm active:scale-95"
                          >
                            <RotateCcw size={14} />
                            <span>Retry Misplaced Steps</span>
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => initiatePracticeMode()}
                          className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-xl text-xs font-black uppercase tracking-wider transition-all flex items-center gap-2 border border-slate-200 dark:border-slate-700"
                        >
                          <RotateCcw size={14} />
                          <span>Restart Whole Test</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setPracticeMode(false);
                            setStudyViewMode('rubric');
                          }}
                          className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-xl text-xs font-black uppercase tracking-wider transition-all flex items-center gap-2 border border-slate-200 dark:border-slate-700"
                        >
                          <FileText size={14} />
                          <span>Study Checklist Rubric</span>
                        </button>
                      </div>
                    </div>

                  </div>

                  {/* Step by step sequential analysis diagram */}
                  <div className="space-y-4 text-left">
                    <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                      Step by Step Procedural Diagnostic
                    </h4>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {practiceResult.analysis.map((item, index) => {
                        let borderStyle = "border-slate-200 dark:border-slate-800";
                        let statusBadge = (
                          <span className="px-2 py-0.5 bg-slate-100 text-slate-400 dark:bg-slate-800 text-[7px] font-black rounded uppercase tracking-wider">
                            Unplaced
                          </span>
                        );
                        
                        if (item.status === 'correct') {
                          borderStyle = "border-emerald-500 bg-emerald-500/5 dark:bg-emerald-950/20";
                          statusBadge = (
                            <span className="px-2 py-0.5 bg-emerald-500 text-white text-[7px] font-black rounded uppercase tracking-wider">
                              Correct Spot
                            </span>
                          );
                        } else if (item.status === 'wrong') {
                          borderStyle = "border-rose-400 bg-rose-400/5 dark:bg-rose-950/20";
                          statusBadge = (
                            <span className="px-2 py-0.5 bg-rose-500 text-white text-[7px] font-black rounded uppercase tracking-wider">
                              Wrong Spot
                            </span>
                          );
                        }

                        return (
                          <div
                            key={index}
                            className={`p-4 border-l-4 rounded-xl flex gap-3 items-start ${borderStyle} transition-all`}
                          >
                            <div className="w-6 h-6 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 text-[10px] font-black flex items-center justify-center shrink-0">
                              {index + 1}
                            </div>

                            <div className="space-y-1 min-w-0 flex-1">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                {statusBadge}
                                <span className="text-[8px] text-slate-400 font-extrabold uppercase">
                                  Expected Step #{index + 1}
                                </span>
                              </div>
                              <p className="text-xs font-black text-slate-900 dark:text-white leading-snug">
                                {item.text}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                </div>
              )}

            </div>
          )}
        </article>
      )}
    </div>
  );
};

/* Page 2: Study Mode - Visual Theme: Clinical Trust */
const renderTakeawayWithBullets = (text: string, accentColorClass: string = 'text-slate-800 dark:text-slate-200') => {
  if (!text) return null;
  const clean = text.replace(/^["'\s]+|["'\s]+$/g, '');
  const sentences = clean.split(/(?<=[.!?])\s+/).filter(Boolean);
  if (sentences.length === 0) return null;
  const takeaway = sentences[0];
  const bullets = sentences.slice(1);

  return (
    <div className="space-y-2 font-sans">
      <p className={`text-xs md:text-sm font-black leading-snug ${accentColorClass}`}>
        {takeaway}
      </p>
      {bullets.length > 0 && (
        <ul className="space-y-1.5 pt-1">
          {bullets.map((b, idx) => (
            <li key={idx} className="flex items-start gap-2 text-[11px] md:text-xs font-semibold text-slate-600 dark:text-slate-300">
              <span className="w-1.5 h-1.5 rounded-full bg-teal-500 shrink-0 mt-1.5" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const renderSkillSlide = (
  skill: SkillSlide, 
  slide: SkillItemSlide, 
  index: number, 
  showRationale: boolean = false, 
  setExpandedImage: (url: string) => void,
  isPrintMode: boolean = false,
  handleSpeakText?: (t: string) => void,
  isSpeaking: boolean = false
) => {
  if (!slide) return null;

  const cardStyle = isPrintMode 
    ? "w-full bg-white relative"
    : "w-full bg-white dark:bg-slate-900 rounded-[2.5rem] border border-slate-200 dark:border-slate-800 shadow-xl overflow-hidden animate-slide-up relative";

  const renderRationale = (text: string) => (showRationale || isPrintMode) && (
    <div className={`mt-8 p-6 rounded-[2rem] relative overflow-hidden print-no-cut ${isPrintMode ? 'bg-slate-50 border border-slate-200' : 'bg-indigo-50 dark:bg-indigo-950/20 border-2 border-indigo-100 dark:border-indigo-900/50 animate-pop'}`}>
        <h4 className={`text-[9px] font-black mb-2 tracking-widest uppercase flex items-center gap-2 ${isPrintMode ? 'text-slate-500' : 'text-indigo-505 dark:text-indigo-400'}`}><Brain size={14}/> Why this matters</h4>
        {renderTakeawayWithBullets(text, isPrintMode ? 'text-slate-700' : 'text-indigo-950 dark:text-indigo-200')}
    </div>
  );

  const speakText = slide.data.visualAlt || slide.data.importance || slide.data.instruction || (slide.data.steps && slide.data.steps.join('. ')) || '';

  switch (slide.type) {
    case 'infographic':
      return (
        <section className={`${cardStyle} ${isPrintMode ? 'p-0 border-none' : ''}`}>
           <div className="flex flex-col flex-1">
              <div className={isPrintMode ? "rounded-[2rem] overflow-hidden bg-slate-950 border border-slate-205" : "relative group bg-slate-100 dark:bg-slate-950 overflow-hidden min-h-[300px] md:min-h-[400px]"}>
                  {slide.data.imageUrl ? (
                      <>
                        <img src={slide.data.imageUrl} className={`w-full h-full ${isPrintMode ? 'print-image-fit' : 'object-contain'} transition-transform duration-1000 group-hover:scale-105`} alt={slide.data.visualAlt || "Procedural visual"} />
                        {!isPrintMode && (
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                <button onClick={() => setExpandedImage(slide.data.imageUrl)} className="p-4 bg-white text-slate-900 rounded-full shadow-2xl hover:scale-110 transition-transform">
                                    <Maximize2 size={24} />
                                </button>
                            </div>
                        )}
                      </>
                  ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center text-slate-700 dark:text-slate-500 gap-4 opacity-40 py-20">
                          <ImageIcon size={64} />
                          <p className="font-black text-xs tracking-widest uppercase">Visual illustration missing</p>
                      </div>
                  )}
              </div>
              <div className={isPrintMode ? "p-8 space-y-6" : "p-4 sm:p-8 md:p-12 border-t border-slate-100 dark:border-slate-800 space-y-6 sm:space-y-10"}>
                  <div className="print-no-cut max-w-4xl">
                     <div className="flex justify-between items-start gap-4">
                       <h2 className="text-xl md:text-2xl font-black text-slate-900 dark:text-white tracking-tight leading-none mb-2">{slide.title || 'Visual Observation Protocol'}</h2>
                       {!isPrintMode && handleSpeakText && speakText && (
                         <button 
                           onClick={() => handleSpeakText(speakText)}
                           className={`p-2 rounded-xl transition-all border ${isSpeaking ? 'bg-rose-50 border-rose-205 text-rose-500' : 'bg-slate-50 dark:bg-slate-950 text-slate-400 border-slate-250 dark:border-slate-850 hover:text-emerald-500'}`}
                         >
                           <Volume2 size={16} />
                         </button>
                       )}
                     </div>
                    {!isPrintMode && <p className="text-slate-400 text-xs font-bold leading-none mt-1">Grounding medical indicators with standard examiner checklists.</p>}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 print-no-cut max-w-4xl">
                      {(slide.data.observations || []).map((obs: string, i: number) => (
                          <div key={i} className="flex gap-4 p-5 bg-slate-50 dark:bg-slate-950/40 rounded-2xl border border-slate-100 dark:border-slate-850 group transition-all hover:border-emerald-500/20">
                              <div className="w-6 h-6 rounded-full bg-white dark:bg-slate-900 border border-slate-205 dark:border-slate-800 flex items-center justify-center shrink-0 font-black text-[10px] shadow-xs text-slate-401">{i+1}</div>
                              <p className="text-[11px] md:text-xs font-bold text-slate-750 dark:text-slate-200 tracking-tight leading-relaxed">"{obs}"</p>
                          </div>
                      ))}
                  </div>
              </div>
           </div>
        </section>
      );

    case 'overview':
      return (
        <section className={`${cardStyle} p-4 sm:p-8 md:p-12 text-center`}>
            <div className="max-w-4xl mx-auto space-y-8">
                {!isPrintMode && (
                    <div className="w-16 h-16 bg-emerald-50 dark:bg-emerald-950/20 text-emerald-600 dark:text-emerald-400 rounded-[1.5rem] flex items-center justify-center mx-auto shadow-sm border border-emerald-100 dark:border-emerald-850">
                        <ShieldCheck size={32} />
                    </div>
                )}
                <div className="space-y-3 print-no-cut">
                    <div className="flex justify-center items-center gap-2">
                      <h2 className="text-2xl md:text-3xl font-black text-slate-900 dark:text-white tracking-tight leading-none">Clinical Significance</h2>
                      {!isPrintMode && handleSpeakText && speakText && (
                         <button onClick={() => handleSpeakText(speakText)} className={`p-1.5 rounded-lg border text-slate-400 hover:text-emerald-500 border-slate-200 dark:border-slate-805 ${isSpeaking ? 'text-rose-500 animate-pulse' : ''}`}><Volume2 size={13}/></button>
                      )}
                    </div>
                    {renderTakeawayWithBullets(slide.data.importance, "text-slate-800 dark:text-slate-100 text-center max-w-2xl mx-auto")}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4">
                    <div className="p-6 md:p-8 bg-indigo-50/50 dark:bg-indigo-950/10 rounded-[2rem] border border-indigo-100 dark:border-indigo-900/50 text-left space-y-2 print-no-cut">
                        <div className="flex items-center gap-2 text-indigo-600 dark:text-indigo-400"><Target size={16}/> <span className="font-black text-[9px] tracking-widest uppercase">Board Scope Objectives</span></div>
                        {renderTakeawayWithBullets(slide.data.examContext, "text-indigo-950 dark:text-indigo-200")}
                    </div>
                    <div className="p-6 md:p-8 bg-rose-50/50 dark:bg-rose-950/10 rounded-[2rem] border border-rose-100 dark:border-rose-900/50 text-left space-y-2 print-no-cut">
                        <div className="flex items-center gap-2 text-rose-600 dark:text-rose-455"><ShieldAlert size={16}/> <span className="font-black text-[9px] tracking-widest uppercase">Safety Integrity Focus</span></div>
                        {renderTakeawayWithBullets(slide.data.safetyFocus, "text-rose-955 dark:text-rose-200")}
                    </div>
                </div>

                {/* ADVANCED NURSE EDUCATOR ENHANCEMENTS FOR OVERVIEW */}
                {(slide.data.clinicalTips || slide.data.examFocus || slide.data.credentiaGuideline || slide.data.prometricGuideline || slide.data.headmasterGuideline) && (
                  <div className="mt-10 border-t border-slate-100 dark:border-slate-800 pt-8 space-y-6 text-left">
                    <h3 className="text-sm font-black text-indigo-600 dark:text-indigo-400 uppercase tracking-widest flex items-center gap-2">
                      <Sparkles size={16} /> 20-Year Nurse Educator Exam & Floor Strategy
                    </h3>
                    
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                      {/* Veteran Nurse Advice */}
                      {slide.data.clinicalTips && (
                        <div className="bg-slate-50 dark:bg-slate-950/40 p-6 rounded-3xl border border-slate-100 dark:border-slate-850 space-y-3">
                          <h4 className="text-[11px] font-black text-indigo-950 dark:text-indigo-300 uppercase tracking-wider flex items-center gap-1.5 border-b border-slate-200 dark:border-slate-800 pb-2">
                            🩺 Hospital Floor Pro Tips
                          </h4>
                          <p className="text-xs font-medium italic text-slate-655 dark:text-slate-350 leading-relaxed">
                            "{slide.data.clinicalTips}"
                          </p>
                        </div>
                      )}

                      {/* Broad Exam Focus Points */}
                      {slide.data.examFocus && (
                        <div className="bg-emerald-50/40 dark:bg-emerald-950/5 p-6 rounded-3xl border border-emerald-100 dark:border-emerald-900/10 space-y-3">
                          <h4 className="text-[11px] font-black text-emerald-800 dark:text-emerald-400 uppercase tracking-wider flex items-center gap-1.5 border-b border-emerald-150 dark:border-emerald-900/20 pb-2">
                            📝 Core Examination Criteria
                          </h4>
                          <p className="text-xs font-medium text-slate-655 dark:text-slate-300 leading-relaxed">
                            {slide.data.examFocus}
                          </p>
                        </div>
                      )}

                      {/* Board Specific Guidelines Comparison */}
                      {(slide.data.credentiaGuideline || slide.data.prometricGuideline || slide.data.headmasterGuideline) && (
                        <div className="bg-amber-50/30 dark:bg-amber-950/5 p-6 rounded-3xl border border-amber-100 dark:border-amber-900/10 space-y-3">
                          <h4 className="text-[11px] font-black text-amber-800 dark:text-amber-400 uppercase tracking-wider flex items-center gap-1.5 border-b border-amber-100 dark:border-amber-900/20 pb-2">
                            ⚖️ Board Specific Rules
                          </h4>
                          <div className="space-y-3 text-[11px]">
                            {slide.data.credentiaGuideline && (
                              <div className="space-y-0.5">
                                <span className="inline-block px-2 py-0.5 rounded-full bg-indigo-100 dark:bg-indigo-900 text-indigo-800 dark:text-indigo-200 text-[8px] font-black uppercase">Credentia / VUE</span>
                                <p className="font-medium text-slate-655 dark:text-slate-350">{slide.data.credentiaGuideline}</p>
                              </div>
                            )}
                            {slide.data.prometricGuideline && (
                              <div className="space-y-0.5 border-t border-dashed border-slate-200 dark:border-slate-800 pt-2">
                                <span className="inline-block px-2 py-0.5 rounded-full bg-violet-100 dark:bg-violet-900 text-violet-800 dark:text-violet-200 text-[8px] font-black uppercase">Prometric</span>
                                <p className="font-medium text-slate-655 dark:text-slate-350">{slide.data.prometricGuideline}</p>
                              </div>
                            )}
                            {slide.data.headmasterGuideline && (
                              <div className="space-y-0.5 border-t border-dashed border-slate-200 dark:border-slate-800 pt-2">
                                <span className="inline-block px-2 py-0.5 rounded-full bg-teal-100 dark:bg-teal-900 text-teal-800 dark:text-teal-200 text-[8px] font-black uppercase">D&S Headmaster</span>
                                <p className="font-medium text-slate-655 dark:text-slate-350">{slide.data.headmasterGuideline}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
            </div>
        </section>
      );

    case 'supplies':
        return (
          <section className={`${cardStyle} p-4 sm:p-8 md:p-12`}>
              <div className="flex items-center justify-between mb-8 pb-5 border-b border-slate-100 dark:border-slate-800 print-no-cut">
                  <div className="flex items-center gap-4">
                      <div className="p-2.5 bg-indigo-50 dark:bg-indigo-950 text-indigo-650 dark:text-indigo-400 rounded-xl"><ClipboardList size={22}/></div>
                      <div>
                          <h2 className="text-xl md:text-2xl font-black text-slate-900 dark:text-white tracking-tight">Clinical Inventory Checklist</h2>
                          {!isPrintMode && <p className="text-[9px] font-black text-slate-400 tracking-widest uppercase">Materials required before commencing procedure</p>}
                      </div>
                  </div>
                  {!isPrintMode && handleSpeakText && speakText && (
                    <button onClick={() => handleSpeakText("Requires supply items: " + (slide.data.items || []).join(', '))} className={`p-2 rounded-xl transition-all border border-slate-205 dark:border-slate-805 text-slate-400 ${isSpeaking ? 'text-rose-500 hover:text-rose-600 animate-pulse' : 'hover:text-emerald-500'}`}><Volume2 size={16} /></button>
                  )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-4xl">
                  {(slide.data.items || []).map((item: string, i: number) => (
                      <div key={i} className="flex items-center gap-3.5 p-5 bg-slate-50 dark:bg-slate-950/40 rounded-2xl border border-slate-100 dark:border-slate-850 shadow-xs hover:border-indigo-500/10 transition-all print-no-cut">
                          <div className="w-2.5 h-2.5 rounded-full bg-indigo-500 shrink-0"></div>
                          <span className="text-xs md:text-sm font-extrabold text-slate-800 dark:text-slate-200 tracking-tight">{item}</span>
                      </div>
                  ))}
              </div>
          </section>
        );

    case 'procedure': {
        const hasAdvancedFields = !!(
          slide.data.clinicalRationale || 
          slide.data.instructorTip || 
          slide.data.criticalPoints || 
          slide.data.examBoardExceptions
        );
        return (
          <section className={`${cardStyle} p-4 sm:p-6 md:p-10 text-left`}>
              <div className="flex justify-between items-center mb-10 pb-6 border-b border-slate-100 dark:border-slate-850 print-no-cut">
                  <div className="flex items-center gap-4">
                      <div className="p-3 bg-emerald-55 border dark:bg-emerald-950/20 text-emerald-600 dark:text-emerald-400 rounded-2xl"><PlayCircle size={24}/></div>
                      <div>
                          <h2 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight leading-none mb-1">{slide.title || 'Phase Step Instruction'}</h2>
                          {!isPrintMode && <p className="text-[10px] font-black text-slate-400 tracking-widest uppercase">Follow exact sequential actions below</p>}
                      </div>
                  </div>
                  {!isPrintMode && handleSpeakText && speakText && (
                    <button onClick={() => handleSpeakText(speakText)} className={`p-2.5 rounded-xl border border-slate-205 dark:border-slate-850 text-slate-400 transition-all ${isSpeaking ? 'text-rose-500 animate-pulse' : 'hover:text-emerald-505'}`}><Volume2 size={16} /></button>
                  )}
              </div>
              
              {slide.data.imageUrl && (
                <div className={`mb-10 rounded-[2.5rem] overflow-hidden border border-slate-205 dark:border-slate-800 bg-slate-950/20 ${isPrintMode ? 'print-image-fit' : 'aspect-video shadow-md relative group'}`}>
                    <img src={slide.data.imageUrl} className="w-full h-full object-contain" alt={slide.data.visualAlt || "Procedural visual"} />
                </div>
              )}

              <div className={hasAdvancedFields ? "grid grid-cols-1 lg:grid-cols-12 gap-8" : "w-full space-y-4 max-w-4xl"}>
                  <div className={hasAdvancedFields ? "lg:col-span-7 space-y-4" : "space-y-4"}>
                      {(slide.data.steps || []).map((step: string, i: number) => (
                          <div key={i} className="flex gap-6 p-6 bg-slate-50 dark:bg-slate-955/35 rounded-[1.8rem] border border-slate-100 dark:border-slate-850 items-start shadow-xs hover:border-emerald-500/10 transition-all print-no-cut">
                              <div className="w-10 h-10 rounded-xl bg-white dark:bg-slate-900 border border-slate-201 dark:border-slate-800 flex items-center justify-center shrink-0 font-black text-xs shadow-inner mt-1">{i+1}</div>
                              <p className="text-xs md:text-sm font-bold text-slate-700 dark:text-slate-250 leading-relaxed tracking-tight pl-1">"{step}"</p>
                          </div>
                      ))}
                  </div>

                  {hasAdvancedFields && (
                    <div className="lg:col-span-5 space-y-6">
                        {/* Critical Failure Point Red Alert */}
                        {slide.data.criticalPoints && (
                          <div className="p-5 bg-rose-50 dark:bg-rose-950/20 border-l-4 border-rose-500 text-rose-900 dark:text-rose-300 rounded-r-[1.5rem] rounded-l-md space-y-2 shadow-xs animate-pulse">
                            <div className="flex items-center gap-2">
                              <ShieldAlert size={18} className="text-rose-600 dark:text-rose-400 shrink-0" />
                              <h4 className="font-black text-[10px] uppercase tracking-wider">Critical Fail Zone</h4>
                            </div>
                            <p className="text-[11px] font-bold leading-normal">
                              {slide.data.criticalPoints}
                            </p>
                          </div>
                        )}

                        {/* Clinical Rationale Box */}
                        {slide.data.clinicalRationale && (
                          <div className="p-6 bg-indigo-50/50 dark:bg-indigo-950/10 border border-indigo-100 dark:border-indigo-900/50 rounded-3xl space-y-2">
                            <div className="flex items-center gap-2 text-indigo-700 dark:text-indigo-400">
                              <Brain size={16} />
                              <h4 className="font-black text-[10px] uppercase tracking-widest">Clinical Nurse Rationale</h4>
                            </div>
                            <p className="text-[11px] font-medium italic text-indigo-950/80 dark:text-slate-350 leading-relaxed">
                              "{slide.data.clinicalRationale}"
                            </p>
                          </div>
                        )}

                        {/* Instructor Tip Box */}
                        {slide.data.instructorTip && (
                          <div className="p-6 bg-emerald-50/50 dark:bg-emerald-950/10 border border-emerald-100 dark:border-emerald-900/50 rounded-3xl space-y-2">
                            <div className="flex items-center gap-2 text-emerald-750 dark:text-emerald-400">
                              <GraduationCap size={16} />
                              <h4 className="font-black text-[10px] uppercase tracking-widest">Veteran Instructor Tip</h4>
                            </div>
                            <p className="text-[11px] font-semibold text-emerald-900 dark:text-slate-350 leading-relaxed">
                              "{slide.data.instructorTip}"
                            </p>
                          </div>
                        )}

                        {/* Exam Board Exceptions / Scoring Diff */}
                        {slide.data.examBoardExceptions && (
                          <div className="p-6 bg-amber-50/50 dark:bg-amber-950/10 border border-amber-100 dark:border-amber-900/50 rounded-3xl space-y-2">
                            <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400">
                              <Activity size={16} />
                              <h4 className="font-black text-[10px] uppercase tracking-widest">Grading Differences</h4>
                            </div>
                            <p className="text-[11px] font-medium text-amber-955/80 dark:text-slate-300 leading-normal">
                              {slide.data.examBoardExceptions}
                            </p>
                          </div>
                        )}
                    </div>
                  )}
              </div>
              {renderRationale(slide.data.visualAlt)}
          </section>
        );
    }

    case 'criticals':
        return (
          <section className={`${cardStyle} p-6 md:p-10 bg-rose-50 dark:bg-rose-950/15 border-rose-200 dark:border-rose-900/60`}>
              <div className="flex items-center justify-between mb-8 pb-6 border-b border-rose-100 dark:border-rose-900/45 text-rose-600 dark:text-rose-440 print-no-cut">
                  <div className="flex items-center gap-4">
                      <div className="p-3 bg-rose-600 text-white rounded-2xl shadow-lg animate-pulse"><ShieldAlert size={28}/></div>
                      <div>
                          <h2 className="text-2xl font-black text-rose-900 dark:text-rose-300 tracking-tight">CRITICAL SAFETY THRESHOLDS</h2>
                          <p className="text-[10px] font-black text-rose-605 uppercase tracking-widest mt-0.5">Any failure on these steps results in automatic disqualification</p>
                      </div>
                  </div>
                  {!isPrintMode && handleSpeakText && speakText && (
                    <button onClick={() => handleSpeakText("Critical items: " + (slide.data.items || []).join('. '))} className={`p-2.5 rounded-xl border border-rose-200 dark:border-rose-900/50 text-rose-500 transition-all ${isSpeaking ? 'bg-white text-rose-600 scale-105' : 'hover:bg-white/20'}`}><Volume2 size={16} /></button>
                  )}
              </div>
              <div className="grid grid-cols-1 gap-4 max-w-4xl">
                  {(slide.data.items || []).map((item: string, i: number) => (
                      <div key={i} className="flex gap-6 p-6 md:p-8 bg-white dark:bg-slate-900 rounded-[2rem] border-2 border-rose-100 dark:border-rose-900/40 shadow-xs print-no-cut hover:border-rose-500 transition-all">
                          <AlertTriangle size={24} className="text-rose-500 shrink-0 mt-1" />
                          <span className="text-sm md:text-base font-black text-rose-950 dark:text-rose-100 leading-snug">"{item}"</span>
                      </div>
                  ))}
              </div>
          </section>
        );

    case 'reporting':
        return (
          <section className={`${cardStyle} p-6 md:p-10`}>
              <div className="space-y-10 max-w-4xl">
                  <div className="space-y-4">
                      <div className="flex justify-between items-center print-no-cut">
                          <div className="flex items-center gap-4">
                              <div className="p-3 bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-405 rounded-xl"><MessageSquare size={28}/></div>
                              <h2 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">Official Reporting Wording</h2>
                          </div>
                          {!isPrintMode && handleSpeakText && speakText && (
                            <button onClick={() => handleSpeakText(slide.data.sampleWording)} className={`p-2 rounded-xl transition-all border border-slate-205 dark:border-slate-805 text-slate-400 ${isSpeaking ? 'text-rose-500 animate-pulse' : 'hover:text-emerald-500'}`}><Volume2 size={16} /></button>
                          )}
                      </div>
                      <div className="p-8 bg-slate-50 dark:bg-slate-950 rounded-[2.5rem] border border-slate-100 dark:border-slate-850 shadow-inner print-no-cut">
                          <p className="text-xs md:text-sm font-bold text-slate-600 dark:text-slate-300 leading-relaxed">"{slide.data.instruction}"</p>
                      </div>
                  </div>
                  <div className="space-y-4">
                      <h4 className="text-[9px] font-black text-slate-400 tracking-widest uppercase ml-1 flex items-center gap-1.5 print-no-cut"><Target size={14} className="text-indigo-505"/> Accurate Verbal Reporting Template</h4>
                      <div className={`p-8 md:p-10 rounded-[2.5rem] border-2 print-no-cut ${isPrintMode ? 'bg-slate-105 border-slate-200 text-slate-950' : 'bg-slate-950 text-emerald-400 dark:text-emerald-300 border-slate-850 shadow-2xl relative overflow-hidden'}`}>
                           <p className="font-extrabold tracking-tight leading-relaxed text-sm md:text-xl">"{slide.data.sampleWording}"</p>
                      </div>
                  </div>
              </div>
          </section>
        );

    case 'completion':
        return (
          <section className={`${cardStyle} p-6 md:p-10 bg-emerald-50 dark:bg-emerald-950/15 border-emerald-202 dark:border-emerald-900/60`}>
              <div className="flex items-center justify-between mb-8 pb-6 border-b border-emerald-100 dark:border-emerald-900/40 text-emerald-600 dark:text-emerald-430 print-no-cut">
                  <div className="flex items-center gap-4">
                      <div className="p-3 bg-emerald-500 text-white rounded-2xl shadow-lg"><CheckCircle2 size={24}/></div>
                      <div>
                          <h2 className="text-2xl font-black tracking-tight text-emerald-950 dark:text-emerald-300">Exit Safety Procedures</h2>
                          <p className="text-[10px] font-black uppercase tracking-widest mt-0.5">Standard clinical concluding checkmarks performed every exam</p>
                      </div>
                  </div>
                  {!isPrintMode && handleSpeakText && speakText && (
                    <button onClick={() => handleSpeakText("Concluding steps: " + (slide.data.items || []).join('. '))} className={`p-2.5 rounded-xl border border-emerald-200 dark:border-emerald-900/50 text-emerald-500 transition-all ${isSpeaking ? 'bg-white text-emerald-650 animate-pulse' : 'hover:bg-white/20'}`}><Volume2 size={16} /></button>
                  )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl">
                  {(slide.data.items || []).map((item: string, i: number) => (
                      <div key={i} className="flex items-center gap-5 p-6 bg-white dark:bg-slate-900 rounded-[1.8rem] border-2 border-emerald-105 dark:border-emerald-900/40 shadow-xs print-no-cut hover:border-emerald-500 transition-all">
                          <CheckCircle size={20} className="text-emerald-504 shrink-0" />
                          <span className="text-xs md:text-sm font-bold text-slate-800 dark:text-slate-205 leading-relaxed">"{item}"</span>
                      </div>
                  ))}
              </div>
          </section>
        );

    case 'embedded':
        return (
          <section className={`${cardStyle} p-6 md:p-10 border-indigo-200 dark:border-indigo-900/60`}>
              <div className="flex items-center justify-between mb-8 pb-6 border-b border-indigo-100 dark:border-indigo-900/45 text-indigo-650 dark:text-indigo-400 print-no-cut">
                  <div className="flex items-center gap-4">
                      <div className="p-3 bg-indigo-600 text-white rounded-2xl shadow-lg"><LinkIcon size={24}/></div>
                      <div>
                          <h2 className="text-2xl font-black tracking-tight text-slate-900 dark:text-white">{slide.title || "Interactive Clinical Media Lab"}</h2>
                          <p className="text-[9px] font-black uppercase tracking-widest mt-0.5 text-slate-500 dark:text-slate-400">Interactive medical simulation & clinical procedure media</p>
                      </div>
                  </div>
              </div>
              <EmbeddedPhasePlayer 
                  embedMode={slide.data?.embedMode || 'url'}
                  embedUrl={slide.data?.embedUrl}
                  embedCode={slide.data?.embedCode}
                  aspectRatio={slide.data?.aspectRatio || '16:9'}
                  embedWidth={slide.data?.embedWidth}
                  embedHeight={slide.data?.embedHeight}
                  title={slide.title}
                  videoTitle={slide.data?.videoTitle}
                  videoAuthor={slide.data?.videoAuthor}
                  videoAuthorUrl={slide.data?.videoAuthorUrl}
                  videoThumbnail={slide.data?.videoThumbnail}
                  videoNotes={slide.data?.videoNotes}
                  videoDescription={slide.data?.videoDescription}
              />
          </section>
        );

    default: return null;
  }
};
